import axios from 'axios';
import { parseMongoDBExtendedJSON } from '../utils/mongodbParser';
// import MockAdapter from 'axios-mock-adapter';

// API instance oluştur
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('businessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Log all requests, especially profile endpoints
    if (config.url?.includes('/business/profile')) {
      console.log('📡 REQUEST INTERCEPTOR:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
        headers: config.headers,
        data: config.data
      });
    }
    return config;
  },
  (error) => {
    console.error('❌ REQUEST INTERCEPTOR ERROR:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    // Log profile endpoint responses
    if (response.config.url?.includes('/business/profile')) {
      console.log('📥 RESPONSE INTERCEPTOR:', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        statusText: response.statusText
      });
      console.log('📥 RESPONSE INTERCEPTOR: Full response data:', JSON.stringify(response.data, null, 2));
      console.log('📥 RESPONSE INTERCEPTOR: Response data keys:', Object.keys(response.data || {}));
    }
    return response;
  },
  async (error) => {
    // Log profile endpoint errors
    if (error.config?.url?.includes('/business/profile')) {
      console.error('📥 RESPONSE INTERCEPTOR ERROR:', {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
    }
    const originalRequest = error.config;
    
    // Eğer refresh-token endpoint'i 401 döndürüyorsa, tekrar request atmamalı
    if (error.response?.status === 401 && originalRequest.url?.includes('/business/refresh-token')) {
      // Refresh token geçersiz, kullanıcıyı login'e yönlendir
      localStorage.removeItem('businessToken');
      localStorage.removeItem('businessRefreshToken');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Handle insufficient credit error (400 with "Kredi yetersiz" message)
    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message;
      if (errorMessage === 'Kredi yetersiz' || errorMessage === 'Insufficient credit') {
        // Extract language from current URL or default to 'tr'
        const currentPath = window.location.pathname;
        const langMatch = currentPath.match(/^\/(tr|en)/);
        const lang = langMatch ? langMatch[1] : 'tr';
        
        // Store credit error data in sessionStorage for display on add-credit page
        if (error.response?.data?.data) {
          sessionStorage.setItem('creditError', JSON.stringify({
            currentBalance: error.response.data.data.currentBalance,
            estimatedCreditCost: error.response.data.data.estimatedCreditCost,
            required: error.response.data.data.required
          }));
        }
        
        // Redirect to add-credit page
        window.location.href = `/${lang}/add-credit`;
        return Promise.reject(error);
      }
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('businessRefreshToken');
      if (refreshToken) {
        try {
          const response = await api.post('/business/refresh-token', { refreshToken });
          const { token, refreshToken: newRefreshToken } = response.data;
          
          localStorage.setItem('businessToken', token);
          localStorage.setItem('businessRefreshToken', newRefreshToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('businessToken');
          localStorage.removeItem('businessRefreshToken');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

type AnalyticsFetcher = (businessId: string) => Promise<any>;

const wrapSuccessResponse = (payload: any) => ({
  data: {
    success: true,
    data: payload,
  },
});

const normalizeAIAnalysisData = (analysis: any) => {
  if (!analysis) {
    return { insights: [] };
  }

  const rawInsights = analysis.insights;
  let insightsArray: any[] = [];

  if (Array.isArray(rawInsights)) {
    insightsArray = rawInsights;
  } else if (rawInsights && typeof rawInsights === 'object') {
    insightsArray = Object.values(rawInsights);
  }

  const normalizedInsights = insightsArray
    .filter(Boolean)
    .map((insight: any) => ({
      ...insight,
      icon: insight?.icon ?? '',
      color: insight?.color ?? '',
    }));

  return {
    ...analysis,
    insights: normalizedInsights,
  };
};

const createAnalyticsFetcher = (platform: string): AnalyticsFetcher => {
  const inFlightRequests: Record<string, Promise<any> | undefined> = {};

  return (businessId: string) => {
    if (!inFlightRequests[businessId]) {
      inFlightRequests[businessId] = api
        .get(`/${platform}/analytics/${businessId}`)
        .then((response) => {
          const rawData = response.data?.data ?? response.data ?? null;
          const parsedData = rawData ? parseMongoDBExtendedJSON(rawData) : rawData;
          delete inFlightRequests[businessId];
          return parsedData;
        })
        .catch((fetchError) => {
          delete inFlightRequests[businessId];
          throw fetchError;
        });
    }

    return inFlightRequests[businessId]!;
  };
};

// Mock adapter oluştur - DEVRE DIŞI BIRAKILDI
// const mock = new MockAdapter(api, { delayResponse: 500 });

// Mock data (analytics, audience, and engagement)
// const mockAnalyticsData = {
//   engagement: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Etkileşim',
//         data: [1200, 1900, 3000, 5000, 2000, 3000],
//         borderColor: 'rgb(168, 85, 247)',
//         backgroundColor: 'rgba(168, 85, 247, 0.1)',
//       }
//     ]
//   },
//   platform: {
//     labels: ['Instagram', 'Facebook', 'Twitter', 'LinkedIn'],
//     datasets: [
//       {
//         label: 'Takipçi Sayısı',
//         data: [12500, 8900, 5600, 3200],
//         backgroundColor: [
//           'rgba(225, 48, 108, 0.8)',
//           'rgba(24, 119, 242, 0.8)',
//           'rgba(29, 161, 242, 0.8)',
//           'rgba(0, 119, 181, 0.8)'
//         ],
//       }
//     ]
//   },
//   bubble: {
//     datasets: [
//       {
//         label: 'Instagram',
//         data: [
//           { x: 1200, y: 5000, r: 20 },
//           { x: 800, y: 3000, r: 15 },
//           { x: 1500, y: 7000, r: 25 }
//         ],
//         backgroundColor: 'rgba(225, 48, 108, 0.6)',
//       },
//       {
//         label: 'Facebook',
//         data: [
//           { x: 900, y: 4000, r: 18 },
//           { x: 1100, y: 6000, r: 22 },
//           { x: 700, y: 2500, r: 12 }
//         ],
//         backgroundColor: 'rgba(24, 119, 242, 0.6)',
//       }
//     ]
//   },
//   platformSpecific: {
//     instagram: {
//       labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//       datasets: [
//         {
//           label: 'Takipçi Artışı',
//           data: [1200, 1900, 3000, 5000, 2000, 3000],
//           borderColor: 'rgb(225, 48, 108)',
//           backgroundColor: 'rgba(225, 48, 108, 0.1)',
//         },
//         {
//           label: 'Etkileşim',
//           data: [800, 1200, 2000, 3500, 1500, 2200],
//           borderColor: 'rgba(225, 48, 108, 0.7)',
//           backgroundColor: 'rgba(225, 48, 108, 0.05)',
//         }
//       ]
//     },
//     facebook: {
//       labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//       datasets: [
//         {
//           label: 'Takipçi Artışı',
//           data: [900, 1500, 2200, 3800, 1800, 2500],
//           borderColor: 'rgb(24, 119, 242)',
//           backgroundColor: 'rgba(24, 119, 242, 0.1)',
//         },
//         {
//           label: 'Etkileşim',
//           data: [600, 1000, 1500, 2800, 1200, 1800],
//           borderColor: 'rgba(24, 119, 242, 0.7)',
//           backgroundColor: 'rgba(24, 119, 242, 0.05)',
//         }
//       ]
//     },
//     twitter: {
//       labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//       datasets: [
//         {
//           label: 'Takipçi Artışı',
//           data: [700, 1200, 1800, 2800, 1400, 2000],
//           borderColor: 'rgb(29, 161, 242)',
//           backgroundColor: 'rgba(29, 161, 242, 0.1)',
//         },
//         {
//           label: 'Etkileşim',
//           data: [500, 800, 1200, 2000, 1000, 1500],
//           borderColor: 'rgba(29, 161, 242, 0.7)',
//           backgroundColor: 'rgba(29, 161, 242, 0.05)',
//         }
//       ]
//     },
//     linkedin: {
//       labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//       datasets: [
//         {
//           label: 'Takipçi Artışı',
//           data: [500, 800, 1200, 2000, 1000, 1500],
//           borderColor: 'rgb(0, 119, 181)',
//           backgroundColor: 'rgba(0, 119, 181, 0.1)',
//         },
//         {
//           label: 'Etkileşim',
//           data: [300, 500, 800, 1400, 700, 1000],
//           borderColor: 'rgba(0, 119, 181, 0.7)',
//           backgroundColor: 'rgba(0, 119, 181, 0.05)',
//         }
//       ]
//     }
//   },
//   aiAnalysis: {
//     engagement: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Etkileşim Artışı',
//           message: 'Son 30 günde etkileşim oranınız %25 artış gösterdi. Bu, içerik stratejinizin başarılı olduğunu gösteriyor.',
//           icon: 'faLightbulb',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Gelecek Tahmini',
//           message: 'Mevcut trend devam ederse, önümüzdeki ay etkileşim oranınız %15 daha artabilir.',
//           icon: 'faBrain',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     platform: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Platform Performansı',
//           message: 'Instagram en güçlü platformunuz. Facebook ve Twitter\'da da potansiyel büyüme alanları mevcut.',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'recommendation',
//           title: 'LinkedIn Stratejisi',
//           message: 'LinkedIn\'de daha fazla profesyonel içerik paylaşarak bu platformdaki performansınızı artırabilirsiniz.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     bubble: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Etkileşim vs Erişim Analizi',
//           message: 'Yüksek erişim oranına sahip içerikleriniz daha fazla etkileşim alıyor. Bu trendi devam ettirin.',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         }
//       ]
//     },
//     instagram: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Instagram Performansı',
//           message: 'Instagram\'da etkileşim oranınız %8.5. Bu platform ortalamasının üzerinde!',
//           icon: 'faChartLine',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     facebook: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Facebook Performansı',
//           message: 'Facebook\'ta etkileşim oranınız %6.2. Video içerikleriniz daha iyi performans gösteriyor.',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         }
//       ]
//     },
//     twitter: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Twitter Performansı',
//           message: 'Twitter\'da etkileşim oranınız %4.8. Tweet sıklığınızı artırabilirsiniz.',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         }
//       ]
//     },
//     linkedin: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'LinkedIn Performansı',
//           message: 'LinkedIn\'de etkileşim oranınız %3.1. Profesyonel içerikleriniz daha iyi performans gösteriyor.',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         }
//       ]
//     }
//   }
// };

// // Mock audience data
// const mockAIPredictionsData = {
//   predictions: {
//     growth: {
//       title: 'Aylık Büyüme Tahminleri',
//       description: 'AI algoritması tarafından analiz edilen sosyal medya büyüme trendleri ve gelecek 3 ay için tahminler',
//       data: {
//         instagram: { current: 12500, predicted: 15200, growth: 21.6 },
//         facebook: { current: 8900, predicted: 10800, growth: 21.3 },
//         twitter: { current: 4200, predicted: 5100, growth: 21.4 },
//         linkedin: { current: 2100, predicted: 2600, growth: 23.8 }
//       }
//     },
//     engagement: {
//       title: 'Etkileşim Trend Tahminleri',
//       description: 'Gelecek dönemler için beklenen etkileşim oranları ve performans öngörüleri',
//       data: {
//         likes: { current: 8.2, predicted: 9.1, growth: 11.0 },
//         comments: { current: 3.4, predicted: 3.8, growth: 11.8 },
//         shares: { current: 1.8, predicted: 2.1, growth: 16.7 },
//         reach: { current: 15.6, predicted: 18.2, growth: 16.7 }
//       }
//     },
//     content: {
//       title: 'İçerik Performans Öngörüleri',
//       description: 'Hangi içerik türlerinin gelecekte daha iyi performans göstereceğine dair AI tahminleri',
//       data: {
//         video: { current: 12.5, predicted: 15.2, growth: 21.6 },
//         image: { current: 8.9, predicted: 10.1, growth: 13.5 },
//         carousel: { current: 6.2, predicted: 7.8, growth: 25.8 },
//         story: { current: 4.1, predicted: 5.2, growth: 26.8 }
//       }
//     },
//     audience: {
//       title: 'Kitle Büyüme Öngörüleri',
//       description: 'Hedef kitle segmentlerinin gelecekteki büyüme potansiyeli ve demografik değişimler',
//       data: {
//         '18-24': { current: 3200, predicted: 3800, growth: 18.8 },
//         '25-34': { current: 4800, predicted: 5700, growth: 18.8 },
//         '35-44': { current: 2100, predicted: 2500, growth: 19.0 },
//         '45+': { current: 1200, predicted: 1400, growth: 16.7 }
//       }
//     }
//   },
//   charts: {
//     growth: {
//       labels: ['Instagram', 'Facebook', 'Twitter', 'LinkedIn'],
//       datasets: [
//         {
//           label: 'Mevcut Takipçi',
//           data: [12500, 8900, 4200, 2100],
//           backgroundColor: 'rgba(29, 161, 242, 0.8)',
//           borderColor: '#1DA1F2',
//           borderWidth: 1
//         },
//         {
//           label: 'Tahmin Edilen',
//           data: [15200, 10800, 5100, 2600],
//           backgroundColor: 'rgba(23, 191, 99, 0.8)',
//           borderColor: '#17BF63',
//           borderWidth: 1
//         }
//       ]
//     },
//     engagement: {
//       labels: ['Beğeni', 'Yorum', 'Paylaşım', 'Erişim'],
//       datasets: [
//         {
//           label: 'Mevcut Oran (%)',
//           data: [8.2, 3.4, 1.8, 15.6],
//           backgroundColor: 'rgba(29, 161, 242, 0.8)',
//           borderColor: '#1DA1F2',
//           borderWidth: 1
//         },
//         {
//           label: 'Tahmin Edilen (%)',
//           data: [9.1, 3.8, 2.1, 18.2],
//           backgroundColor: 'rgba(23, 191, 99, 0.8)',
//           borderColor: '#17BF63',
//           borderWidth: 1
//         }
//       ]
//     },
//     content: {
//       labels: ['Video', 'Görsel', 'Carousel', 'Story'],
//       datasets: [
//         {
//           label: 'Mevcut Performans',
//           data: [12.5, 8.9, 6.2, 4.1],
//           backgroundColor: 'rgba(29, 161, 242, 0.8)',
//           borderColor: '#1DA1F2',
//           borderWidth: 1
//         },
//         {
//           label: 'Tahmin Edilen',
//           data: [15.2, 10.1, 7.8, 5.2],
//           backgroundColor: 'rgba(23, 191, 99, 0.8)',
//           borderColor: '#17BF63',
//           borderWidth: 1
//         }
//       ]
//     },
//     audience: {
//       labels: ['18-24', '25-34', '35-44', '45+'],
//       datasets: [
//         {
//           label: 'Mevcut Kitle',
//           data: [3200, 4800, 2100, 1200],
//           backgroundColor: 'rgba(29, 161, 242, 0.8)',
//           borderColor: '#1DA1F2',
//           borderWidth: 1
//         },
//         {
//           label: 'Tahmin Edilen',
//           data: [3800, 5700, 2500, 1400],
//           backgroundColor: 'rgba(23, 191, 99, 0.8)',
//           borderColor: '#17BF63',
//           borderWidth: 1
//         }
//       ]
//     }
//   },
//   aiAnalysis: {
//     growth: {
//       insights: [
//         {
//           type: 'prediction',
//           title: 'Büyüme Trendi',
//           message: 'Tüm platformlarda %20+ büyüme bekleniyor. LinkedIn en yüksek potansiyeli gösteriyor.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         },
//         {
//           type: 'insight',
//           title: 'Platform Performansı',
//           message: 'Instagram ve Facebook ana büyüme motorları olmaya devam edecek.',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'warning',
//           title: 'Rekabet Artışı',
//           message: 'Twitter\'da rekabet yoğunluğu artabilir, içerik kalitesine odaklanın.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     engagement: {
//       insights: [
//         {
//           type: 'prediction',
//           title: 'Etkileşim Artışı',
//           message: 'Tüm etkileşim türlerinde %10+ artış bekleniyor.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         },
//         {
//           type: 'insight',
//           title: 'Paylaşım Trendi',
//           message: 'Paylaşım oranlarında en yüksek artış (%16.7) bekleniyor.',
//           icon: 'faShare',
//           color: 'text-blue-400'
//         }
//       ]
//     },
//     content: {
//       insights: [
//         {
//           type: 'prediction',
//           title: 'Video Dominansı',
//           message: 'Video içerikler %21.6 artışla en yüksek performansı gösterecek.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         },
//         {
//           type: 'insight',
//           title: 'Story Potansiyeli',
//           message: 'Story formatında %26.8 artış bekleniyor, bu formata odaklanın.',
//           icon: 'faLightbulb',
//           color: 'text-blue-400'
//         }
//       ]
//     },
//     audience: {
//       insights: [
//         {
//           type: 'prediction',
//           title: 'Genç Kitle Artışı',
//           message: '18-24 yaş grubunda %18.8 büyüme bekleniyor.',
//           icon: 'faUsers',
//           color: 'text-green-400'
//         },
//         {
//           type: 'insight',
//           title: 'Demografik Denge',
//           message: 'Tüm yaş gruplarında dengeli büyüme trendi devam ediyor.',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         }
//       ]
//     }
//   }
// };

// const mockTwitterData = {
//   account: {
//     username: '@winnomi',
//     displayName: 'Winnomi',
//     followers: 8500,
//     following: 1200,
//     tweets: 1240,
//     profilePicture: 'https://via.placeholder.com/100x100/1DA1F2/FFFFFF?text=W',
//     bio: 'sosyal medya analitikleri ve AI destekli içerik önerileri 🚀 #AI #Analytics',
//     website: 'www.winnomi.com',
//     isVerified: true,
//     location: 'İstanbul, Türkiye',
//     joinDate: 'Ocak 2023'
//   },
//   recentTweets: [
//     {
//       id: 1,
//       content: 'Yeni AI özelliklerimizi keşfedin! 🚀 #AI #SosyalMedya #Teknoloji #Analytics',
//       likes: 245,
//       retweets: 89,
//       replies: 34,
//       reach: 3200,
//       engagement: 12.5,
//       postedAt: '2 saat önce',
//       hashtags: ['#AI', '#SosyalMedya', '#Teknoloji', '#Analytics']
//     },
//     {
//       id: 2,
//       content: 'Müşteri memnuniyeti analizlerimiz 📊 #Analytics #Data #CustomerExperience',
//       likes: 189,
//       retweets: 67,
//       replies: 28,
//       reach: 2800,
//       engagement: 10.8,
//       postedAt: '5 saat önce',
//       hashtags: ['#Analytics', '#Data', '#CustomerExperience']
//     },
//     {
//       id: 3,
//       content: 'sosyal medya trendleri 2024 📈 #Trends #Marketing #SocialMedia',
//       likes: 320,
//       retweets: 145,
//       replies: 56,
//       reach: 4200,
//       engagement: 15.2,
//       postedAt: '1 gün önce',
//       hashtags: ['#Trends', '#Marketing', '#SocialMedia']
//     }
//   ],
//   topTweets: [
//     {
//       id: 1,
//       content: 'AI destekli sosyal medya analizi',
//       likes: 320,
//       retweets: 145,
//       replies: 56,
//       reach: 4200,
//       engagement: 15.2,
//       postedAt: '1 hafta önce',
//       hashtags: ['#AI', '#Analytics']
//     },
//     {
//       id: 2,
//       content: 'sosyal medya trendleri 2024',
//       likes: 289,
//       retweets: 123,
//       replies: 45,
//       reach: 3800,
//       engagement: 14.1,
//       postedAt: '2 hafta önce',
//       hashtags: ['#Trends', '#Marketing']
//     },
//     {
//       id: 3,
//       content: 'Müşteri memnuniyeti analizi',
//       likes: 245,
//       retweets: 89,
//       replies: 34,
//       reach: 3200,
//       engagement: 12.5,
//       postedAt: '3 hafta önce',
//       hashtags: ['#Analytics', '#CustomerExperience']
//     }
//   ],
//   engagement: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Etkileşim Oranı (%)',
//         data: [8.5, 9.2, 10.1, 11.5, 12.8, 13.2],
//         borderColor: '#1DA1F2',
//         backgroundColor: 'rgba(29, 161, 242, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#1DA1F2',
//         pointBorderColor: '#1DA1F2',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   reach: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Erişim Sayısı',
//         data: [2800, 3200, 3800, 4200, 4800, 5200],
//         borderColor: '#17BF63',
//         backgroundColor: 'rgba(23, 191, 99, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#17BF63',
//         pointBorderColor: '#17BF63',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   hashtags: {
//     labels: ['#AI', '#Analytics', '#Marketing', '#Tech', '#SocialMedia'],
//     datasets: [
//       {
//         label: 'Kullanım Sayısı',
//         data: [156, 134, 98, 87, 76],
//         backgroundColor: [
//           'rgba(29, 161, 242, 0.8)',
//           'rgba(23, 191, 99, 0.8)',
//           'rgba(255, 173, 31, 0.8)',
//           'rgba(255, 122, 89, 0.8)',
//           'rgba(138, 43, 226, 0.8)'
//         ],
//         borderColor: [
//           '#1DA1F2',
//           '#17BF63',
//           '#FFAD1F',
//           '#FF7A59',
//           '#8A2BE2'
//         ],
//         borderWidth: 1
//       }
//     ]
//   },
//   sentiment: {
//     labels: ['Pozitif', 'Nötr', 'Negatif'],
//     datasets: [
//       {
//         data: [82, 14, 4],
//         backgroundColor: [
//           'rgba(23, 191, 99, 0.8)',
//           'rgba(107, 114, 128, 0.8)',
//           'rgba(244, 33, 46, 0.8)'
//         ],
//         borderColor: [
//           '#17BF63',
//           '#6B7280',
//           '#F4212E'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   aiAnalysis: {
//     engagement: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Twitter Engagement Performansı',
//           message: 'Twitter hesabınızda %13.2 ortalama engagement oranı var. Bu çok iyi bir performans!',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Büyüme Öngörüsü',
//           message: 'Mevcut trend devam ederse, gelecek ay takipçi sayınız %20 artabilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         },
//         {
//           type: 'warning',
//           title: 'İçerik Çeşitliliği',
//           message: 'Daha fazla video ve görsel içerik paylaşarak engagement oranınızı artırabilirsiniz.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     reach: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Twitter Reach Analizi',
//           message: 'Erişim sayınız son 3 ayda %85 artış gösterdi. Bu harika bir büyüme!',
//           icon: 'faUsers',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Reach Optimizasyonu',
//           message: 'Trending hashtag\'leri kullanarak organik erişiminizi %30 yükseltebilirsiniz.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     hashtags: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Hashtag Performansı',
//           message: '#AI hashtag\'iniz en yüksek etkileşim alıyor.',
//           icon: 'faHashtag',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Hashtag Stratejisi',
//           message: 'Daha fazla AI ve teknoloji hashtag\'i kullanmayı deneyin.',
//           icon: 'faLightbulb',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     sentiment: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Sentiment Analizi',
//           message: 'Takipçilerinizin %82\'si pozitif tepki veriyor. Bu mükemmel bir oran!',
//           icon: 'faHeart',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'warning',
//           title: 'Negatif Feedback',
//           message: 'Negatif yorumların %60\'ı teknik konularla ilgili.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     }
//   }
// };

// const mockLinkedInData = {
//   account: {
//     name: 'Winnomi',
//     headline: 'sosyal medya Analitikleri ve AI Destekli İçerik Önerileri',
//     connections: 2500,
//     followers: 1800,
//     posts: 89,
//     profilePicture: 'https://via.placeholder.com/100x100/0A66C2/FFFFFF?text=W',
//     company: 'Winnomi Technologies',
//     location: 'İstanbul, Türkiye',
//     industry: 'Teknoloji',
//     isVerified: true,
//     summary: 'sosyal medya analitikleri ve AI destekli içerik önerileri konusunda uzmanlaşmış teknoloji şirketi. Müşterilerimize en iyi sosyal medya deneyimini sunuyoruz.'
//   },
//   recentPosts: [
//     {
//       id: 1,
//       content: 'Yeni AI özelliklerimizi keşfedin! 🚀 sosyal medya yönetiminde devrim yaratan teknolojilerimizle tanışın. #AI #SosyalMedya #Teknoloji #Innovation',
//       likes: 125,
//       comments: 23,
//       shares: 12,
//       reach: 1800,
//       engagement: 8.9,
//       postedAt: '3 saat önce',
//       type: 'text'
//     },
//     {
//       id: 2,
//       content: 'Müşteri memnuniyeti analizlerimiz 📊 Veri odaklı kararlar almak için doğru araçları kullanın. #Analytics #Data #CustomerExperience #BusinessIntelligence',
//       likes: 98,
//       comments: 18,
//       shares: 8,
//       reach: 1500,
//       engagement: 8.3,
//       postedAt: '1 gün önce',
//       type: 'text'
//     },
//     {
//       id: 3,
//       content: 'sosyal medya trendleri 2024 📈 İş dünyasında sosyal medyanın geleceği hakkında düşüncelerimiz. #Trends #Marketing #SocialMedia #FutureOfWork',
//       likes: 156,
//       comments: 34,
//       shares: 19,
//       reach: 2200,
//       engagement: 9.5,
//       postedAt: '2 gün önce',
//       type: 'text'
//     }
//   ],
//   topContent: [
//     {
//       id: 1,
//       content: 'AI destekli içerik önerileri',
//       likes: 156,
//       comments: 34,
//       shares: 19,
//       reach: 2200,
//       engagement: 9.5,
//       postedAt: '1 hafta önce',
//       type: 'text'
//     },
//     {
//       id: 2,
//       content: 'sosyal medya analitikleri',
//       likes: 134,
//       comments: 28,
//       shares: 15,
//       reach: 1950,
//       engagement: 9.1,
//       postedAt: '2 hafta önce',
//       type: 'text'
//     },
//     {
//       id: 3,
//       content: 'Müşteri memnuniyeti',
//       likes: 112,
//       comments: 22,
//       shares: 11,
//       reach: 1680,
//       engagement: 8.6,
//       postedAt: '3 hafta önce',
//       type: 'text'
//     }
//   ],
//   engagement: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Etkileşim Oranı (%)',
//         data: [7.2, 7.8, 8.1, 8.5, 8.9, 9.2],
//         borderColor: '#0A66C2',
//         backgroundColor: 'rgba(10, 102, 194, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#0A66C2',
//         pointBorderColor: '#0A66C2',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   reach: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Erişim Sayısı',
//         data: [1200, 1350, 1420, 1580, 1680, 1800],
//         borderColor: '#8B5CF6',
//         backgroundColor: 'rgba(139, 92, 246, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#8B5CF6',
//         pointBorderColor: '#8B5CF6',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   contentType: {
//     labels: ['Metin', 'Görsel', 'Video', 'Makale', 'Anket'],
//     datasets: [
//       {
//         label: 'Ortalama Etkileşim',
//         data: [8.9, 7.2, 9.5, 6.8, 8.1],
//         backgroundColor: [
//           'rgba(10, 102, 194, 0.8)',
//           'rgba(139, 92, 246, 0.8)',
//           'rgba(16, 185, 129, 0.8)',
//           'rgba(245, 158, 11, 0.8)',
//           'rgba(239, 68, 68, 0.8)'
//         ],
//         borderColor: [
//           '#0A66C2',
//           '#8B5CF6',
//           '#10B981',
//           '#F59E0B',
//           '#EF4444'
//         ],
//         borderWidth: 1
//       }
//     ]
//   },
//   sentiment: {
//     labels: ['Pozitif', 'Nötr', 'Negatif'],
//     datasets: [
//       {
//         data: [78, 18, 4],
//         backgroundColor: [
//           'rgba(16, 185, 129, 0.8)',
//           'rgba(107, 114, 128, 0.8)',
//           'rgba(239, 68, 68, 0.8)'
//         ],
//         borderColor: [
//           '#10B981',
//           '#6B7280',
//           '#EF4444'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   aiAnalysis: {
//     engagement: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Etkileşim Trend Analizi',
//           message: 'LinkedIn profilinizde etkileşim oranı son 6 ayda %28 artış gösterdi. Bu profesyonel bir performans!',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Gelecek Ay Öngörüsü',
//           message: 'Mevcut trend devam ederse, gelecek ay etkileşim oranınız %15 artabilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         },
//         {
//           type: 'warning',
//           title: 'İçerik Çeşitliliği',
//           message: 'Video içerikleriniz metin içeriklerinden daha iyi performans gösteriyor.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     reach: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Erişim Analizi',
//           message: 'Erişim sayınız son 3 ayda %50 artış gösterdi. Bu harika bir büyüme!',
//           icon: 'faUsers',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Büyüme Öngörüsü',
//           message: 'Mevcut hızla devam ederseniz, 3 ay içinde 2.5K takipçiye ulaşabilirsiniz.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     contentType: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'İçerik Performansı',
//           message: 'Video içerikleriniz en yüksek etkileşim oranına sahip (%9.5).',
//           icon: 'faCheckCircle',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'İçerik Stratejisi',
//           message: 'Daha fazla video ve anket içerik üretmeye odaklanın.',
//           icon: 'faLightbulb',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     sentiment: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Sentiment Analizi',
//           message: 'Bağlantılarınızın %78\'i pozitif tepki veriyor. Bu çok iyi bir oran!',
//           icon: 'faHeart',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'warning',
//           title: 'Negatif Feedback',
//           message: 'Negatif yorumların %75\'i teknik konularla ilgili.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     }
//   }
// };

// const mockFacebookData = {
//   account: {
//     pageName: 'Winnomi',
//     username: '@winnomi',
//     followers: 18500,
//     likes: 19200,
//     posts: 156,
//     profilePicture: 'https://via.placeholder.com/100x100/1877F2/FFFFFF?text=W',
//     bio: 'sosyal medya analitikleri ve AI destekli içerik önerileri',
//     website: 'www.winnomi.com',
//     isVerified: true,
//     category: 'Teknoloji'
//   },
//   recentPosts: [
//     {
//       id: 1,
//       image: 'https://via.placeholder.com/300x300/1877F2/FFFFFF?text=Post+1',
//       caption: 'Yeni AI özelliklerimizi keşfedin! 🚀 #AI #SosyalMedya #Teknoloji',
//       likes: 2450,
//       comments: 156,
//       shares: 89,
//       reach: 12500,
//       engagement: 18.2,
//       postedAt: '3 saat önce',
//       type: 'image'
//     },
//     {
//       id: 2,
//       image: 'https://via.placeholder.com/300x300/1877F2/FFFFFF?text=Video',
//       caption: 'Müşteri memnuniyeti analizlerimiz 📊 #Analytics #Data',
//       likes: 1890,
//       comments: 98,
//       shares: 67,
//       reach: 9800,
//       engagement: 16.5,
//       postedAt: '1 gün önce',
//       type: 'video'
//     },
//     {
//       id: 3,
//       image: 'https://via.placeholder.com/300x300/1877F2/FFFFFF?text=Post+3',
//       caption: 'sosyal medya trendleri 2024 📈 #Trends #Marketing',
//       likes: 3200,
//       comments: 234,
//       shares: 145,
//       reach: 15800,
//       engagement: 22.1,
//       postedAt: '2 gün önce',
//       type: 'image'
//     }
//   ],
//   topContent: [
//     {
//       id: 1,
//       image: 'https://via.placeholder.com/200x200/1877F2/FFFFFF?text=Top+1',
//       caption: 'AI destekli içerik önerileri',
//       likes: 3200,
//       comments: 234,
//       shares: 145,
//       reach: 15800,
//       engagement: 22.1,
//       postedAt: '1 hafta önce',
//       type: 'image'
//     },
//     {
//       id: 2,
//       image: 'https://via.placeholder.com/200x200/1877F2/FFFFFF?text=Top+2',
//       caption: 'sosyal medya analitikleri',
//       likes: 2800,
//       comments: 180,
//       shares: 95,
//       reach: 13500,
//       engagement: 20.8,
//       postedAt: '2 hafta önce',
//       type: 'video'
//     },
//     {
//       id: 3,
//       image: 'https://via.placeholder.com/200x200/1877F2/FFFFFF?text=Top+3',
//       caption: 'Müşteri memnuniyeti',
//       likes: 2200,
//       comments: 145,
//       shares: 78,
//       reach: 11200,
//       engagement: 18.2,
//       postedAt: '3 hafta önce',
//       type: 'image'
//     }
//   ],
//   engagement: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Etkileşim Oranı (%)',
//         data: [16.2, 17.8, 18.5, 19.2, 20.1, 21.5],
//         borderColor: '#1877F2',
//         backgroundColor: 'rgba(24, 119, 242, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#1877F2',
//         pointBorderColor: '#1877F2',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   reach: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Erişim Sayısı',
//         data: [12500, 13800, 14200, 15800, 16500, 18500],
//         borderColor: '#8B5CF6',
//         backgroundColor: 'rgba(139, 92, 246, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#8B5CF6',
//         pointBorderColor: '#8B5CF6',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   contentPerformance: {
//     labels: ['Fotoğraf', 'Video', 'Link', 'Status', 'Event'],
//     datasets: [
//       {
//         label: 'Ortalama Etkileşim',
//         data: [18.5, 22.1, 15.8, 12.4, 19.2],
//         backgroundColor: [
//           'rgba(24, 119, 242, 0.8)',
//           'rgba(139, 92, 246, 0.8)',
//           'rgba(16, 185, 129, 0.8)',
//           'rgba(245, 158, 11, 0.8)',
//           'rgba(239, 68, 68, 0.8)'
//         ],
//         borderColor: [
//           '#1877F2',
//           '#8B5CF6',
//           '#10B981',
//           '#F59E0B',
//           '#EF4444'
//         ],
//         borderWidth: 1
//       }
//     ]
//   },
//   sentiment: {
//     labels: ['Pozitif', 'Nötr', 'Negatif'],
//     datasets: [
//       {
//         data: [72, 20, 8],
//         backgroundColor: [
//           'rgba(16, 185, 129, 0.8)',
//           'rgba(107, 114, 128, 0.8)',
//           'rgba(239, 68, 68, 0.8)'
//         ],
//         borderColor: [
//           '#10B981',
//           '#6B7280',
//           '#EF4444'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   aiAnalysis: {
//     engagement: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Etkileşim Trend Analizi',
//           message: 'Facebook sayfanızda etkileşim oranı son 6 ayda %32 artış gösterdi. Bu mükemmel bir performans!',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Gelecek Ay Öngörüsü',
//           message: 'Mevcut trend devam ederse, gelecek ay etkileşim oranınız %25 artabilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         },
//         {
//           type: 'warning',
//           title: 'İçerik Çeşitliliği',
//           message: 'Video içerikleriniz fotoğraflardan daha iyi performans gösteriyor.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     reach: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Erişim Analizi',
//           message: 'Erişim sayınız son 3 ayda %48 artış gösterdi. Bu harika bir büyüme!',
//           icon: 'faUsers',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Büyüme Öngörüsü',
//           message: 'Mevcut hızla devam ederseniz, 3 ay içinde 25K takipçiye ulaşabilirsiniz.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     contentPerformance: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'İçerik Performansı',
//           message: 'Video içerikleriniz en yüksek etkileşim oranına sahip (%22.1).',
//           icon: 'faCheckCircle',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'İçerik Stratejisi',
//           message: 'Daha fazla video ve event içerik üretmeye odaklanın.',
//           icon: 'faLightbulb',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     sentiment: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Sentiment Analizi',
//           message: 'Takipçilerinizin %72\'si pozitif tepki veriyor. Bu çok iyi bir oran!',
//           icon: 'faHeart',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'warning',
//           title: 'Negatif Feedback',
//           message: 'Negatif yorumların %70\'i teknik sorunlarla ilgili.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     }
//   }
// };

// const mockInstagramData = {
//   account: {
//     username: '@winnomi_official',
//     followers: 12500,
//     following: 850,
//     posts: 234,
//     profilePicture: 'https://via.placeholder.com/100x100/E4405F/FFFFFF?text=W',
//     bio: 'sosyal medya analitikleri ve AI destekli içerik önerileri',
//     website: 'www.winnomi.com',
//     isVerified: true
//   },
//   recentPosts: [
//     {
//       id: 1,
//       image: 'https://via.placeholder.com/300x300/E4405F/FFFFFF?text=Post+1',
//       caption: 'Yeni AI özelliklerimizi keşfedin! 🚀 #AI #SosyalMedya',
//       likes: 1250,
//       comments: 89,
//       shares: 45,
//       reach: 8500,
//       engagement: 15.2,
//       postedAt: '2 saat önce'
//     },
//     {
//       id: 2,
//       image: 'https://via.placeholder.com/300x300/E4405F/FFFFFF?text=Post+2',
//       caption: 'Müşteri memnuniyeti analizlerimiz 📊 #Analytics',
//       likes: 980,
//       comments: 67,
//       shares: 32,
//       reach: 7200,
//       engagement: 12.8,
//       postedAt: '1 gün önce'
//     },
//     {
//       id: 3,
//       image: 'https://via.placeholder.com/300x300/E4405F/FFFFFF?text=Post+3',
//       caption: 'sosyal medya trendleri 2024 📈 #Trends',
//       likes: 2100,
//       comments: 156,
//       shares: 78,
//       reach: 12000,
//       engagement: 18.5,
//       postedAt: '3 gün önce'
//     }
//   ],
//   topContent: [
//     {
//       id: 1,
//       image: 'https://via.placeholder.com/200x200/E4405F/FFFFFF?text=Top+1',
//       caption: 'AI destekli içerik önerileri',
//       likes: 2100,
//       comments: 156,
//       shares: 78,
//       reach: 12000,
//       engagement: 18.5,
//       postedAt: '1 hafta önce'
//     },
//     {
//       id: 2,
//       image: 'https://via.placeholder.com/200x200/E4405F/FFFFFF?text=Top+2',
//       caption: 'sosyal medya analitikleri',
//       likes: 1800,
//       comments: 120,
//       shares: 65,
//       reach: 10500,
//       engagement: 16.8,
//       postedAt: '2 hafta önce'
//     },
//     {
//       id: 3,
//       image: 'https://via.placeholder.com/200x200/E4405F/FFFFFF?text=Top+3',
//       caption: 'Müşteri memnuniyeti',
//       likes: 1500,
//       comments: 95,
//       shares: 45,
//       reach: 9000,
//       engagement: 14.2,
//       postedAt: '3 hafta önce'
//     }
//   ],
//   engagement: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Etkileşim Oranı (%)',
//         data: [12.5, 14.2, 13.8, 15.6, 16.2, 17.8],
//         borderColor: '#E4405F',
//         backgroundColor: 'rgba(228, 64, 95, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#E4405F',
//         pointBorderColor: '#E4405F',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   reach: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Erişim Sayısı',
//         data: [8500, 9200, 8800, 10500, 11200, 12500],
//         borderColor: '#8B5CF6',
//         backgroundColor: 'rgba(139, 92, 246, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#8B5CF6',
//         pointBorderColor: '#8B5CF6',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   contentPerformance: {
//     labels: ['Fotoğraf', 'Video', 'Reel', 'Story', 'IGTV'],
//     datasets: [
//       {
//         label: 'Ortalama Etkileşim',
//         data: [15.2, 18.5, 22.1, 12.8, 16.4],
//         backgroundColor: [
//           'rgba(228, 64, 95, 0.8)',
//           'rgba(139, 92, 246, 0.8)',
//           'rgba(16, 185, 129, 0.8)',
//           'rgba(245, 158, 11, 0.8)',
//           'rgba(59, 130, 246, 0.8)'
//         ],
//         borderColor: [
//           '#E4405F',
//           '#8B5CF6',
//           '#10B981',
//           '#F59E0B',
//           '#3B82F6'
//         ],
//         borderWidth: 1
//       }
//     ]
//   },
//   sentiment: {
//     labels: ['Pozitif', 'Nötr', 'Negatif'],
//     datasets: [
//       {
//         data: [65, 25, 10],
//         backgroundColor: [
//           'rgba(16, 185, 129, 0.8)',
//           'rgba(107, 114, 128, 0.8)',
//           'rgba(239, 68, 68, 0.8)'
//         ],
//         borderColor: [
//           '#10B981',
//           '#6B7280',
//           '#EF4444'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   aiAnalysis: {
//     engagement: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Etkileşim Trend Analizi',
//           message: 'Etkileşim oranınız son 6 ayda %42 artış gösterdi. Bu mükemmel bir performans!',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Gelecek Ay Öngörüsü',
//           message: 'Mevcut trend devam ederse, gelecek ay etkileşim oranınız %20 artabilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         },
//         {
//           type: 'warning',
//           title: 'İçerik Çeşitliliği',
//           message: 'Video içerikleriniz fotoğraflardan daha iyi performans gösteriyor.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     reach: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Erişim Analizi',
//           message: 'Erişim sayınız son 3 ayda %35 artış gösterdi. Bu harika bir büyüme!',
//           icon: 'faUsers',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Büyüme Öngörüsü',
//           message: 'Mevcut hızla devam ederseniz, 3 ay içinde 15K takipçiye ulaşabilirsiniz.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     contentPerformance: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'İçerik Performansı',
//           message: 'Reel içerikleriniz en yüksek etkileşim oranına sahip (%22.1).',
//           icon: 'faCheckCircle',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'İçerik Stratejisi',
//           message: 'Daha fazla Reel ve Video içerik üretmeye odaklanın.',
//           icon: 'faLightbulb',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     sentiment: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Sentiment Analizi',
//           message: 'Takipçilerinizin %65\'i pozitif tepki veriyor. Bu çok iyi bir oran!',
//           icon: 'faHeart',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'warning',
//           title: 'Negatif Feedback',
//           message: 'Negatif yorumların %60\'ı müşteri hizmetleri ile ilgili.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     }
//   }
// };

// const mockEngagementData = {
//   sentiment: {
//     labels: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'],
//     datasets: [
//       {
//         label: 'Instagram',
//         data: [0.65, 0.72, 0.68, 0.75, 0.78, 0.71, 0.69],
//         borderColor: '#E4405F',
//         backgroundColor: 'rgba(228, 64, 95, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#E4405F',
//         pointBorderColor: '#E4405F',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       },
//       {
//         label: 'Facebook',
//         data: [0.58, 0.65, 0.62, 0.68, 0.71, 0.66, 0.64],
//         borderColor: '#1877F2',
//         backgroundColor: 'rgba(24, 119, 242, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#1877F2',
//         pointBorderColor: '#1877F2',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       },
//       {
//         label: 'Twitter',
//         data: [0.45, 0.52, 0.48, 0.55, 0.58, 0.53, 0.51],
//         borderColor: '#1DA1F2',
//         backgroundColor: 'rgba(29, 161, 242, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#1DA1F2',
//         pointBorderColor: '#1DA1F2',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       },
//       {
//         label: 'LinkedIn',
//         data: [0.72, 0.78, 0.75, 0.81, 0.84, 0.79, 0.77],
//         borderColor: '#0A66C2',
//         backgroundColor: 'rgba(10, 102, 194, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#0A66C2',
//         pointBorderColor: '#0A66C2',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       },
//       {
//         label: 'Genel Toplam',
//         data: [0.60, 0.67, 0.63, 0.70, 0.73, 0.68, 0.66],
//         borderColor: '#8B5CF6',
//         backgroundColor: 'rgba(139, 92, 246, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#8B5CF6',
//         pointBorderColor: '#8B5CF6',
//         pointRadius: 6,
//         pointHoverRadius: 8,
//         borderWidth: 3
//       }
//     ]
//   },
//   responseTime: {
//     labels: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'],
//     datasets: [
//       {
//         label: 'Ortalama Yanıt Süresi (dakika)',
//         data: [15, 12, 18, 10, 8, 20, 25],
//         backgroundColor: 'rgba(16, 185, 129, 0.8)',
//         borderColor: '#10B981',
//         borderWidth: 1
//       }
//     ]
//   },
//   problemSolving: {
//     labels: ['Çözüldü', 'Çözülmedi', 'Beklemede', 'İptal'],
//     datasets: [
//       {
//         data: [75, 15, 8, 2],
//         backgroundColor: [
//           'rgba(16, 185, 129, 0.8)',
//           'rgba(239, 68, 68, 0.8)',
//           'rgba(245, 158, 11, 0.8)',
//           'rgba(107, 114, 128, 0.8)'
//         ],
//         borderColor: [
//           '#10B981',
//           '#EF4444',
//           '#F59E0B',
//           '#6B7280'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   feedbackType: {
//     labels: ['Pozitif', 'Negatif', 'Nötr', 'Öneri', 'Şikayet'],
//     datasets: [
//       {
//         data: [45, 15, 25, 10, 5],
//         backgroundColor: [
//           'rgba(16, 185, 129, 0.8)',
//           'rgba(239, 68, 68, 0.8)',
//           'rgba(107, 114, 128, 0.8)',
//           'rgba(59, 130, 246, 0.8)',
//           'rgba(245, 158, 11, 0.8)'
//         ],
//         borderColor: [
//           '#10B981',
//           '#EF4444',
//           '#6B7280',
//           '#3B82F6',
//           '#F59E0B'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   trend: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Etkileşim Trendi',
//         data: [1200, 1500, 1800, 2200, 2500, 2800],
//         borderColor: '#8B5CF6',
//         backgroundColor: 'rgba(139, 92, 246, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#8B5CF6',
//         pointBorderColor: '#8B5CF6',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       },
//       {
//         label: 'Memnuniyet Oranı',
//         data: [65, 68, 72, 75, 78, 82],
//         borderColor: '#10B981',
//         backgroundColor: 'rgba(16, 185, 129, 0.1)',
//         tension: 0.4,
//         pointBackgroundColor: '#10B981',
//         pointBorderColor: '#10B981',
//         pointRadius: 4,
//         pointHoverRadius: 6
//       }
//     ]
//   },
//   aiAnalysis: {
//     sentiment: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Etkileşim Sentiment Analizi',
//           message: 'Müşteri etkileşimleriniz genel olarak pozitif. Instagram\'da en yüksek memnuniyet oranı görülüyor.',
//           icon: 'faLightbulb',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Memnuniyet Trend Öngörüsü',
//           message: 'Mevcut trend devam ederse, gelecek hafta müşteri memnuniyetiniz %8-12 artabilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         },
//         {
//           type: 'warning',
//           title: 'Twitter Dikkat',
//           message: 'Twitter\'da müşteri şikayetleri artıyor. Hızlı müdahale gerekli.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     responseTime: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Yanıt Süresi Analizi',
//           message: 'Hafta içi yanıt süreleriniz ortalamanın altında. Bu müşteri memnuniyetini artırıyor.',
//           icon: 'faClock',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Performans Öngörüsü',
//           message: 'Mevcut hızla devam ederseniz, aylık ortalama yanıt süreniz 12 dakikaya düşebilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     problemSolving: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Problem Çözme Başarısı',
//           message: 'Problem çözme oranınız %75. Bu sektör ortalamasının üzerinde.',
//           icon: 'faCheckCircle',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'İyileştirme Potansiyeli',
//           message: 'Bekleyen problemlerin %60\'ı 24 saat içinde çözülebilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     feedbackType: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Feedback Dağılımı',
//           message: 'Pozitif feedback oranınız %45. Bu iyi bir oran ancak geliştirilebilir.',
//           icon: 'faStar',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'warning',
//           title: 'Negatif Feedback Artışı',
//           message: 'Negatif feedback oranı son haftada %5 artış gösterdi.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     trend: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Trend Analizi',
//           message: 'Etkileşim ve memnuniyet trendleriniz pozitif yönde ilerliyor.',
//           icon: 'faChartLine',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Büyüme Öngörüsü',
//           message: 'Mevcut trend devam ederse, 3 ay içinde etkileşim oranınız %40 artabilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-green-400'
//         }
//       ]
//     }
//   }
// };

// const mockAudienceData = {
//   demographics: {
//     ageGroups: {
//       labels: ['18-24', '25-34', '35-44', '45-54', '55+'],
//       datasets: [
//         {
//           label: 'Yaş Dağılımı',
//           data: [25, 35, 20, 15, 5],
//           backgroundColor: [
//             'rgba(255, 99, 132, 0.8)',
//             'rgba(54, 162, 235, 0.8)',
//             'rgba(255, 206, 86, 0.8)',
//             'rgba(75, 192, 192, 0.8)',
//             'rgba(153, 102, 255, 0.8)'
//           ],
//           borderColor: [
//             'rgb(255, 99, 132)',
//             'rgb(54, 162, 235)',
//             'rgb(255, 206, 86)',
//             'rgb(75, 192, 192)',
//             'rgb(153, 102, 255)'
//           ],
//           borderWidth: 2
//         }
//       ]
//     },
//     gender: {
//       labels: ['Kadın', 'Erkek', 'Diğer'],
//       datasets: [
//         {
//           label: 'Cinsiyet Dağılımı',
//           data: [65, 30, 5],
//           backgroundColor: [
//             'rgba(255, 99, 132, 0.8)',
//             'rgba(54, 162, 235, 0.8)',
//             'rgba(255, 206, 86, 0.8)'
//           ],
//           borderColor: [
//             'rgb(255, 99, 132)',
//             'rgb(54, 162, 235)',
//             'rgb(255, 206, 86)'
//           ],
//           borderWidth: 2
//         }
//       ]
//     },
//     location: {
//       labels: ['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya', 'Diğer'],
//       datasets: [
//         {
//           label: 'Lokasyon Dağılımı',
//           data: [40, 20, 15, 10, 8, 7],
//           backgroundColor: [
//             'rgba(255, 99, 132, 0.8)',
//             'rgba(54, 162, 235, 0.8)',
//             'rgba(255, 206, 86, 0.8)',
//             'rgba(75, 192, 192, 0.8)',
//             'rgba(153, 102, 255, 0.8)',
//             'rgba(255, 159, 64, 0.8)'
//           ],
//           borderColor: [
//             'rgb(255, 99, 132)',
//             'rgb(54, 162, 235)',
//             'rgb(255, 206, 86)',
//             'rgb(75, 192, 192)',
//             'rgb(153, 102, 255)',
//             'rgb(255, 159, 64)'
//           ],
//           borderWidth: 2
//         }
//       ]
//     }
//   },
//   growth: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Takipçi Artışı',
//         data: [1200, 1900, 3000, 5000, 2000, 3000],
//         borderColor: 'rgb(168, 85, 247)',
//         backgroundColor: 'rgba(168, 85, 247, 0.1)',
//         borderWidth: 3,
//         fill: true,
//         tension: 0.4
//       },
//       {
//         label: 'Aktif Kullanıcılar',
//         data: [800, 1200, 2000, 3500, 1500, 2200],
//         borderColor: 'rgba(168, 85, 247, 0.7)',
//         backgroundColor: 'rgba(168, 85, 247, 0.05)',
//         borderWidth: 2,
//         fill: false,
//         tension: 0.4
//       }
//     ]
//   },
//   engagement: {
//     labels: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'],
//     datasets: [
//       {
//         label: 'Günlük Etkileşim',
//         data: [1200, 1400, 1600, 1800, 2000, 2200, 2400],
//         borderColor: 'rgb(34, 197, 94)',
//         backgroundColor: 'rgba(34, 197, 94, 0.1)',
//         borderWidth: 3,
//         fill: true,
//         tension: 0.4
//       }
//     ]
//   },
//   interests: {
//     labels: ['Teknoloji', 'Spor', 'Moda', 'Yemek', 'Seyahat', 'Eğitim', 'Sağlık'],
//     datasets: [
//       {
//         label: 'İlgi Alanları',
//         data: [30, 25, 20, 15, 10, 8, 7],
//         backgroundColor: [
//           'rgba(255, 99, 132, 0.8)',
//           'rgba(54, 162, 235, 0.8)',
//           'rgba(255, 206, 86, 0.8)',
//           'rgba(75, 192, 192, 0.8)',
//           'rgba(153, 102, 255, 0.8)',
//           'rgba(255, 159, 64, 0.8)',
//           'rgba(199, 199, 199, 0.8)'
//         ],
//         borderColor: [
//           'rgb(255, 99, 132)',
//           'rgb(54, 162, 235)',
//           'rgb(255, 206, 86)',
//           'rgb(75, 192, 192)',
//           'rgb(153, 102, 255)',
//           'rgb(255, 159, 64)',
//           'rgb(199, 199, 199)'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   sentiment: {
//     labels: ['Pozitif', 'Nötr', 'Negatif'],
//     datasets: [
//       {
//         label: 'Duygu Analizi',
//         data: [70, 20, 10],
//         backgroundColor: [
//           'rgba(34, 197, 94, 0.8)',
//           'rgba(156, 163, 175, 0.8)',
//           'rgba(239, 68, 68, 0.8)'
//         ],
//         borderColor: [
//           'rgb(34, 197, 94)',
//           'rgb(156, 163, 175)',
//           'rgb(239, 68, 68)'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   aiAnalysis: {
//     demographics: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Yaş Dağılımı',
//           message: 'Kitle\'nizin %60\'ı 25-44 yaş aralığında. Bu demografik grup için optimize edilmiş içerik üretmeye odaklanın.',
//           icon: 'faUsers',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Büyüme Potansiyeli',
//           message: '18-24 yaş grubunda büyüme potansiyeli yüksek. Bu gruba yönelik içerik stratejisi geliştirin.',
//           icon: 'faChartLine',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     growth: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Büyüme Trendi',
//           message: 'Son 3 ayda takipçi artışınız %150. Bu hızlı büyüme trendini sürdürmek için içerik kalitesini koruyun.',
//           icon: 'faArrowTrendUp',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'recommendation',
//           title: 'Aktiflik Stratejisi',
//           message: 'Aktif kullanıcı oranınız %75. Bu oranı artırmak için daha interaktif içerikler üretin.',
//           icon: 'faLightbulb',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     engagement: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Hafta Sonu Performansı',
//           message: 'Hafta sonları etkileşim oranınız %20 daha yüksek. Bu günlerde daha fazla içerik paylaşın.',
//           icon: 'faCalendar',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Günlük Trend',
//           message: 'Pazartesi günleri etkileşim düşük. Bu günlerde daha çekici içeriklerle başlayın.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     }
//   }
// };

// Mock endpoints - DEVRE DIŞI BIRAKILDI

// mock.onGet('/analytics/engagement').reply(200, {
//   success: true,
//   data: mockAnalyticsData.engagement
// });

// mock.onGet('/analytics/platform').reply(200, {
//   success: true,
//   data: mockAnalyticsData.platform
// });

// mock.onGet('/analytics/bubble').reply(200, {
//   success: true,
//   data: mockAnalyticsData.bubble
// });

// mock.onGet('/analytics/platform/instagram').reply(200, {
//   success: true,
//   data: mockAnalyticsData.platformSpecific.instagram
// });

// mock.onGet('/analytics/platform/facebook').reply(200, {
//   success: true,
//   data: mockAnalyticsData.platformSpecific.facebook
// });

// mock.onGet('/analytics/platform/twitter').reply(200, {
//   success: true,
//   data: mockAnalyticsData.platformSpecific.twitter
// });

// mock.onGet('/analytics/platform/linkedin').reply(200, {
//   success: true,
//   data: mockAnalyticsData.platformSpecific.linkedin
// });

// mock.onGet("/analytics/ai-analysis/engagement").reply(200, {
//   success: true,
//   data: mockAnalyticsData.aiAnalysis.engagement || []
// });

// mock.onGet("/analytics/ai-analysis/platform").reply(200, {
//   success: true,
//   data: mockAnalyticsData.aiAnalysis.platform || []
// });

// mock.onGet("/analytics/ai-analysis/bubble").reply(200, {
//   success: true,
//   data: mockAnalyticsData.aiAnalysis.bubble || []
// });

// mock.onGet("/analytics/ai-analysis/instagram").reply(200, {
//   success: true,
//   data: mockAnalyticsData.aiAnalysis.instagram || []
// });

// mock.onGet("/analytics/ai-analysis/facebook").reply(200, {
//   success: true,
//   data: mockAnalyticsData.aiAnalysis.facebook || []
// });

// mock.onGet("/analytics/ai-analysis/twitter").reply(200, {
//   success: true,
//   data: mockAnalyticsData.aiAnalysis.twitter || []
// });

// mock.onGet("/analytics/ai-analysis/linkedin").reply(200, {
//   success: true,
//   data: mockAnalyticsData.aiAnalysis.linkedin || []
// });

// mock.onGet(/\/analytics\/ai-analysis\/\w+/).reply(200, (config: any) => {
//   const chartType = config.url?.split('/').pop();
//   console.log('chartType', chartType);
//   console.log('mockAnalyticsData.aiAnalysis', mockAnalyticsData.aiAnalysis[chartType as keyof typeof mockAnalyticsData.aiAnalysis]);
  
//   return [200, {
//     success: true,
//     data: mockAnalyticsData.aiAnalysis[chartType as keyof typeof mockAnalyticsData.aiAnalysis] || []
//   }];
// });

// Audience endpoints
// mock.onGet('/audience/demographics/age').reply(200, {
//   success: true,
//   data: mockAudienceData.demographics.ageGroups
// });

// mock.onGet('/audience/demographics/gender').reply(200, {
//   success: true,
//   data: mockAudienceData.demographics.gender
// });

// mock.onGet('/audience/demographics/location').reply(200, {
//   success: true,
//   data: mockAudienceData.demographics.location
// });

// mock.onGet('/audience/growth').reply(200, {
//   success: true,
//   data: mockAudienceData.growth
// });

// mock.onGet('/audience/engagement').reply(200, {
//   success: true,
//   data: mockAudienceData.engagement
// });

// mock.onGet('/audience/interests').reply(200, {
//   success: true,
//   data: mockAudienceData.interests
// });

// mock.onGet('/audience/sentiment').reply(200, {
//   success: true,
//   data: mockAudienceData.sentiment
// });

// mock.onGet('/audience/ai-analysis/demographics').reply(200, {
//   success: true,
//   data: mockAudienceData.aiAnalysis.demographics || []
// });

// mock.onGet('/audience/ai-analysis/growth').reply(200, {
//   success: true,
//   data: mockAudienceData.aiAnalysis.growth || []
// });


// mock.onGet('/audience/ai-analysis/engagement').reply(200, {
//   success: true,
//   data: mockAudienceData.aiAnalysis.engagement || []
// });


// // Engagement endpoints
// mock.onGet('/engagement/sentiment').reply(200, {
//   success: true,
//   data: mockEngagementData.sentiment
// });

// mock.onGet('/engagement/response-time').reply(200, {
//   success: true,
//   data: mockEngagementData.responseTime
// });

// mock.onGet('/engagement/problem-solving').reply(200, {
//   success: true,
//   data: mockEngagementData.problemSolving
// });

// mock.onGet('/engagement/feedback-type').reply(200, {
//   success: true,
//   data: mockEngagementData.feedbackType
// });

// mock.onGet('/engagement/trend').reply(200, {
//   success: true,
//   data: mockEngagementData.trend
// });

// mock.onGet('/engagement/ai-analysis/sentiment').reply(200, {
//   success: true,
//   data: mockEngagementData.aiAnalysis.sentiment || []
// });

// mock.onGet('/engagement/ai-analysis/response-time').reply(200, {
//   success: true,
//   data: mockEngagementData.aiAnalysis.responseTime || []
// });

// mock.onGet('/engagement/ai-analysis/problem-solving').reply(200, {
//   success: true,
//   data: mockEngagementData.aiAnalysis.problemSolving || []
// });

// mock.onGet('/engagement/ai-analysis/feedback-type').reply(200, {
//   success: true,
//   data: mockEngagementData.aiAnalysis.feedbackType || []
// });

// mock.onGet('/engagement/ai-analysis/trend').reply(200, {
//   success: true,
//   data: mockEngagementData.aiAnalysis.trend || []
// });

// // Instagram endpoints
// mock.onGet('/instagram/account').reply(200, {
//   success: true,
//   data: mockInstagramData.account
// });

// mock.onGet('/instagram/recent-posts').reply(200, {
//   success: true,
//   data: mockInstagramData.recentPosts
// });

// mock.onGet('/instagram/top-content').reply(200, {
//   success: true,
//   data: mockInstagramData.topContent
// });

// mock.onGet('/instagram/engagement').reply(200, {
//   success: true,
//   data: mockInstagramData.engagement
// });

// mock.onGet('/instagram/reach').reply(200, {
//   success: true,
//   data: mockInstagramData.reach
// });

// mock.onGet('/instagram/content-performance').reply(200, {
//   success: true,
//   data: mockInstagramData.contentPerformance
// });

// mock.onGet('/instagram/sentiment').reply(200, {
//   success: true,
//   data: mockInstagramData.sentiment
// });

// mock.onGet('/instagram/ai-analysis/engagement').reply(200, {
//   success: true,
//   data: mockInstagramData.aiAnalysis.engagement || []
// });

// mock.onGet('/instagram/ai-analysis/reach').reply(200, {
//   success: true,
//   data: mockInstagramData.aiAnalysis.reach || []
// });

// mock.onGet('/instagram/ai-analysis/content-performance').reply(200, {
//   success: true,
//   data: mockInstagramData.aiAnalysis.contentPerformance || []
// });

// mock.onGet('/instagram/ai-analysis/sentiment').reply(200, {
//   success: true,
//   data: mockInstagramData.aiAnalysis.sentiment || []
// });

// // Facebook endpoints
// mock.onGet('/facebook/account').reply(200, {
//   success: true,
//   data: mockFacebookData.account
// });

// mock.onGet('/facebook/recent-posts').reply(200, {
//   success: true,
//   data: mockFacebookData.recentPosts
// });

// mock.onGet('/facebook/top-content').reply(200, {
//   success: true,
//   data: mockFacebookData.topContent
// });

// mock.onGet('/facebook/engagement').reply(200, {
//   success: true,
//   data: mockFacebookData.engagement
// });

// mock.onGet('/facebook/reach').reply(200, {
//   success: true,
//   data: mockFacebookData.reach
// });

// mock.onGet('/facebook/content-performance').reply(200, {
//   success: true,
//   data: mockFacebookData.contentPerformance
// });

// mock.onGet('/facebook/sentiment').reply(200, {
//   success: true,
//   data: mockFacebookData.sentiment
// });

// mock.onGet('/facebook/ai-analysis/engagement').reply(200, {
//   success: true,
//   data: mockFacebookData.aiAnalysis.engagement || []
// });

// mock.onGet('/facebook/ai-analysis/reach').reply(200, {
//   success: true,
//   data: mockFacebookData.aiAnalysis.reach || []
// });

// mock.onGet('/facebook/ai-analysis/content-performance').reply(200, {
//   success: true,
//   data: mockFacebookData.aiAnalysis.contentPerformance || []
// });

// mock.onGet('/facebook/ai-analysis/sentiment').reply(200, {
//   success: true,
//   data: mockFacebookData.aiAnalysis.sentiment || []
// });

// mock.onGet('/facebook/ai-analysis/content-performance ').reply(200, {
//   success: true,
//   data: mockFacebookData.aiAnalysis.contentPerformance || []
// });

// // LinkedIn endpoints
// mock.onGet('/linkedin/account').reply(200, {
//   success: true,
//   data: mockLinkedInData.account
// });

// mock.onGet('/linkedin/recent-posts').reply(200, {
//   success: true,
//   data: mockLinkedInData.recentPosts
// });

// mock.onGet('/linkedin/top-content').reply(200, {
//   success: true,
//   data: mockLinkedInData.topContent
// });

// mock.onGet('/linkedin/engagement').reply(200, {
//   success: true,
//   data: mockLinkedInData.engagement
// });

// mock.onGet('/linkedin/reach').reply(200, {
//   success: true,
//   data: mockLinkedInData.reach
// });

// mock.onGet('/linkedin/content-type').reply(200, {
//   success: true,
//   data: mockLinkedInData.contentType
// });

// mock.onGet('/linkedin/sentiment').reply(200, {
//   success: true,
//   data: mockLinkedInData.sentiment
// });

// mock.onGet('/linkedin/ai-analysis/engagement').reply(200, {
//   success: true,
//   data: mockLinkedInData.aiAnalysis.engagement || []
// });

// mock.onGet('/linkedin/ai-analysis/reach').reply(200, {
//   success: true,
//   data: mockLinkedInData.aiAnalysis.reach || []
// });


// mock.onGet('/linkedin/ai-analysis/content-type').reply(200, {
//   success: true,
//   data: mockLinkedInData.aiAnalysis.contentType || []
// });

// mock.onGet('/linkedin/ai-analysis/sentiment').reply(200, {
//   success: true,
//   data: mockLinkedInData.aiAnalysis.sentiment || []
// });

// // Twitter endpoints
// mock.onGet('/twitter/account').reply(200, {
//   success: true,
//   data: mockTwitterData.account
// });

// mock.onGet('/twitter/recent-tweets').reply(200, {
//   success: true,
//   data: mockTwitterData.recentTweets
// });

// mock.onGet('/twitter/top-tweets').reply(200, {
//   success: true,
//   data: mockTwitterData.topTweets
// });

// mock.onGet('/twitter/engagement').reply(200, {
//   success: true,
//   data: mockTwitterData.engagement
// });

// mock.onGet('/twitter/reach').reply(200, {
//   success: true,
//   data: mockTwitterData.reach
// });

// mock.onGet('/twitter/hashtags').reply(200, {
//   success: true,
//   data: mockTwitterData.hashtags
// });

// mock.onGet('/twitter/sentiment').reply(200, {
//   success: true,
//   data: mockTwitterData.sentiment
// });

// mock.onGet('/twitter/ai-analysis/engagement').reply(200, {
//   success: true,
//   data: mockTwitterData.aiAnalysis.engagement || []
// });

// mock.onGet('/twitter/ai-analysis/reach').reply(200, {
//   success: true,
//   data: mockTwitterData.aiAnalysis.reach || []
// });

// mock.onGet('/twitter/ai-analysis/hashtags').reply(200, {
//   success: true,
//   data: mockTwitterData.aiAnalysis.hashtags || []
// });

// mock.onGet('/twitter/ai-analysis/sentiment').reply(200, {
//   success: true,
//   data: mockTwitterData.aiAnalysis.sentiment || []
// });

// // AI Predictions endpoints
// mock.onGet('/ai-predictions/predictions').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.predictions
// });

// mock.onGet('/ai-predictions/charts/growth').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.charts.growth || []
// });

// mock.onGet('/ai-predictions/charts/engagement').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.charts.engagement || []
// });

// mock.onGet('/ai-predictions/charts/content').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.charts.content || []
// });

// mock.onGet('/ai-predictions/charts/audience').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.charts.audience || []
// });

// mock.onGet('/ai-predictions/ai-analysis/growth').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.aiAnalysis.growth || []
// });

// mock.onGet('/ai-predictions/ai-analysis/engagement').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.aiAnalysis.engagement || []
// });

// mock.onGet('/ai-predictions/ai-analysis/content').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.aiAnalysis.content || []
// });

// mock.onGet('/ai-predictions/ai-analysis/audience').reply(200, {
//   success: true,
//   data: mockAIPredictionsData.aiAnalysis.audience || []
// });

// API fonksiyonları
export const analyticsAPI = {
  // MongoDB extended JSON formatını parse eden wrapper fonksiyonlar
  getEngagementData: async (businessId: string) => {
    const response = await api.get(`/analytics/engagement/${businessId}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data)
      }
    };
  },
  getPlatformData: async (businessId: string) => {
    const response = await api.get(`/analytics/platform/${businessId}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data)
      }
    };
  },
  getBubbleData: async (businessId: string) => {
    const response = await api.get(`/analytics/bubble/${businessId}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data)
      }
    };
  },
  getPlatformSpecificData: async (businessId: string, platform: string) => {
    const response = await api.get(`/analytics/platform/${businessId}/${platform}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data)
      }
    };
  },
  getAIAnalysis: async (businessId: string, chartType: string) => {
    const response = await api.get(`/analytics/ai-analysis/${businessId}/${chartType}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data)
      }
    };
  },
  // Tüm analytics verisini tek seferde getiren endpoint (MongoDB document'ı direkt)
  getAllAnalyticsData: async (businessId: string, type: string = 'analytics') => {
    const response = await api.get(`/analytics/${businessId}/${type}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data || response.data)
      }
    };
  },
};

export const audienceAPI = {
  getAgeDemographics: (businessId: string) => api.get(`/audience/demographics/age/${businessId}`),
  getGenderDemographics: (businessId: string) => api.get(`/audience/demographics/gender/${businessId}`),
  getLocationDemographics: (businessId: string) => api.get(`/audience/demographics/location/${businessId}`),
  getGrowthData: (businessId: string) => api.get(`/audience/growth/${businessId}`),
  getEngagementData: (businessId: string) => api.get(`/audience/engagement/${businessId}`),
  getInterestsData: (businessId: string) => api.get(`/audience/interests/${businessId}`),
  getSentimentData: (businessId: string) => api.get(`/audience/sentiment/${businessId}`),
  getAIAnalysis: (businessId: string, chartType: string) => api.get(`/audience/ai-analysis/${businessId}/${chartType}`),
  // Tüm audience verisini tek seferde getiren endpoint
  getAllAudienceData: async (businessId: string, type: string = 'audience') => {
    const response = await api.get(`/analytics/${businessId}/${type}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data || response.data)
      }
    };
  },
};

export const engagementAPI = {
  getSentimentData: (businessId: string) => api.get(`/engagement/sentiment/${businessId}`),
  getResponseTimeData: (businessId: string) => api.get(`/engagement/response-time/${businessId}`),
  getProblemSolvingData: (businessId: string) => api.get(`/engagement/problem-solving/${businessId}`),
  getFeedbackTypeData: (businessId: string) => api.get(`/engagement/feedback-type/${businessId}`),
  getTrendData: (businessId: string) => api.get(`/engagement/trend/${businessId}`),
  getFeedbacksData: (businessId: string) => api.get(`/engagement/feedbacks/${businessId}`),
  getAIAnalysis: (businessId: string, chartType: string) => api.get(`/engagement/ai-analysis/${businessId}/${chartType}`),
  // Tüm engagement verisini tek seferde getiren endpoint
  getAllEngagementData: async (businessId: string, type: string = 'engagement') => {
    const response = await api.get(`/analytics/${businessId}/${type}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data || response.data)
      }
    };
  },
};

const fetchInstagramAnalytics = createAnalyticsFetcher('instagram');

export const instagramAPI = {
  getAnalytics: (businessId: string) => api.get(`/instagram/analytics/${businessId}`),
  getAccountData: async (businessId: string) => api.get(`/instagram/account/${businessId}`),
  getRecentPosts: async (businessId: string) => api.get(`/instagram/recent-posts/${businessId}`),
  getTopContent: async (businessId: string) => api.get(`/instagram/top-content/${businessId}`),
  getEngagementData: async (businessId: string) =>
    wrapSuccessResponse((await fetchInstagramAnalytics(businessId))?.engagement ?? null),
  getReachData: async (businessId: string) =>
    wrapSuccessResponse((await fetchInstagramAnalytics(businessId))?.reach ?? null),
  getHashtagsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchInstagramAnalytics(businessId))?.hashtags ?? null),
  getContentPerformanceData: async (businessId: string) =>
    wrapSuccessResponse((await fetchInstagramAnalytics(businessId))?.contentPerformance ?? null),
  getSentimentData: async (businessId: string) =>
    wrapSuccessResponse((await fetchInstagramAnalytics(businessId))?.sentiment ?? null),
  getProblemsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchInstagramAnalytics(businessId))?.problems ?? []),
  getAIAnalysis: async (businessId: string, chartType: string) =>
    wrapSuccessResponse(
      normalizeAIAnalysisData((await fetchInstagramAnalytics(businessId))?.aiAnalysis?.[chartType])
    ),
};

const fetchFacebookAnalytics = createAnalyticsFetcher('facebook');

export const facebookAPI = {
  getAnalytics: (businessId: string) => api.get(`/facebook/analytics/${businessId}`),
  getAccountData: async (businessId: string) => api.get(`/facebook/account/${businessId}`),
  getRecentPosts: async (businessId: string) => api.get(`/facebook/recent-posts/${businessId}`),
  getTopContent: async (businessId: string) => api.get(`/facebook/top-content/${businessId}`),
  getEngagementData: async (businessId: string) =>
    wrapSuccessResponse((await fetchFacebookAnalytics(businessId))?.engagement ?? null),
  getReachData: async (businessId: string) =>
    wrapSuccessResponse((await fetchFacebookAnalytics(businessId))?.reach ?? null),
  getHashtagsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchFacebookAnalytics(businessId))?.hashtags ?? null),
  getContentPerformanceData: async (businessId: string) =>
    wrapSuccessResponse((await fetchFacebookAnalytics(businessId))?.contentPerformance ?? null),
  getSentimentData: async (businessId: string) =>
    wrapSuccessResponse((await fetchFacebookAnalytics(businessId))?.sentiment ?? null),
  getProblemsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchFacebookAnalytics(businessId))?.problems ?? []),
  getAIAnalysis: async (businessId: string, chartType: string) =>
    wrapSuccessResponse(
      normalizeAIAnalysisData((await fetchFacebookAnalytics(businessId))?.aiAnalysis?.[chartType])
    ),
};

const fetchLinkedInAnalytics = createAnalyticsFetcher('linkedin');

export const linkedInAPI = {
  getAnalytics: (businessId: string) => api.get(`/linkedin/analytics/${businessId}`),
  getAccountData: async (businessId: string) => api.get(`/linkedin/account/${businessId}`),
  getRecentPosts: async (businessId: string) => api.get(`/linkedin/recent-posts/${businessId}`),
  getTopContent: async (businessId: string) => api.get(`/linkedin/top-content/${businessId}`),
  getEngagementData: async (businessId: string) =>
    wrapSuccessResponse((await fetchLinkedInAnalytics(businessId))?.engagement ?? null),
  getReachData: async (businessId: string) =>
    wrapSuccessResponse((await fetchLinkedInAnalytics(businessId))?.reach ?? null),
  getHashtagsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchLinkedInAnalytics(businessId))?.hashtags ?? null),
  getContentPerformanceData: async (businessId: string) =>
    wrapSuccessResponse((await fetchLinkedInAnalytics(businessId))?.contentPerformance ?? null),
  getSentimentData: async (businessId: string) =>
    wrapSuccessResponse((await fetchLinkedInAnalytics(businessId))?.sentiment ?? null),
  getProblemsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchLinkedInAnalytics(businessId))?.problems ?? []),
  getAIAnalysis: async (businessId: string, chartType: string) =>
    wrapSuccessResponse(
      normalizeAIAnalysisData((await fetchLinkedInAnalytics(businessId))?.aiAnalysis?.[chartType])
    ),
};

const fetchTwitterAnalytics = createAnalyticsFetcher('twitter');

export const twitterAPI = {
  getAnalytics: (businessId: string) => api.get(`/twitter/analytics/${businessId}`),
  getAccountData: async (businessId: string) => api.get(`/twitter/account/${businessId}`),
  getRecentTweets: async (businessId: string) => api.get(`/twitter/recent-posts/${businessId}`),
  // Keep function name for compatibility, but fetch from unified endpoint
  getTopTweets: async (businessId: string) => api.get(`/twitter/top-content/${businessId}`),
  getEngagementData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTwitterAnalytics(businessId))?.engagement ?? null),
  getReachData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTwitterAnalytics(businessId))?.reach ?? null),
  getHashtagsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTwitterAnalytics(businessId))?.hashtags ?? null),
  getContentPerformanceData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTwitterAnalytics(businessId))?.contentPerformance ?? null),
  getSentimentData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTwitterAnalytics(businessId))?.sentiment ?? null),
  getProblemsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTwitterAnalytics(businessId))?.problems ?? []),
  getAIAnalysis: async (businessId: string, chartType: string) =>
    wrapSuccessResponse(
      normalizeAIAnalysisData((await fetchTwitterAnalytics(businessId))?.aiAnalysis?.[chartType])
    ),
};

// AI Recommendations mock data
// const mockAIRecommendationsData = {
//   recommendations: {
//     growth: {
//       title: 'Büyüme Önerileri',
//       description: 'sosyal medya hesaplarınızın büyümesi için AI tarafından önerilen stratejiler',
//       recommendations: [
//         {
//           id: 'reels-strategy',
//           platform: 'Instagram',
//           priority: 'Yüksek',
//           title: 'Reels Stratejisi Geliştirin',
//           description: 'Reels içerikleriniz %40 daha fazla etkileşim alıyor. Günlük 2-3 Reels paylaşımı öneriliyor.',
//           impact: '+25% Etkileşim',
//           effort: 'Orta',
//           timeline: '2 hafta',
//           status: 'pending',
//           progress: 0,
//           icon: 'faVideo',
//           color: 'text-pink-500',
//           bgColor: 'bg-pink-500/20',
//           category: 'growth'
//         },
//         {
//           id: 'linkedin-content',
//           platform: 'LinkedIn',
//           priority: 'Yüksek',
//           title: 'Profesyonel İçerik Artırın',
//           description: 'Endüstri analizleri ve profesyonel görüşler %60 daha fazla paylaşım alıyor.',
//           impact: '+35% Paylaşım',
//           effort: 'Düşük',
//           timeline: '1 hafta',
//           status: 'pending',
//           progress: 0,
//           icon: 'faBullhorn',
//           color: 'text-blue-600',
//           bgColor: 'bg-blue-600/20',
//           category: 'growth'
//         },
//         {
//           id: 'hashtag-strategy',
//           platform: 'Instagram',
//           priority: 'Orta',
//           title: 'Hashtag Stratejisi Optimize Edin',
//           description: 'Trend hashtag\'ler ve niş etiketler kullanarak erişiminizi %30 artırın.',
//           impact: '+30% Erişim',
//           effort: 'Düşük',
//           timeline: '3 gün',
//           status: 'pending',
//           progress: 0,
//           icon: 'faHashtag',
//           color: 'text-purple-500',
//           bgColor: 'bg-purple-500/20',
//           category: 'growth'
//         }
//       ]
//     },
//     engagement: {
//       title: 'Etkileşim Önerileri',
//       description: 'Takipçilerinizle daha iyi etkileşim kurmanız için AI önerileri',
//       recommendations: [
//         {
//           id: 'story-interaction',
//           platform: 'Instagram',
//           priority: 'Yüksek',
//           title: 'Story Etkileşimini Artırın',
//           description: 'Günlük story paylaşımları ve etkileşimli özellikler kullanın.',
//           impact: '+40% Etkileşim',
//           effort: 'Düşük',
//           timeline: '1 hafta',
//           status: 'pending',
//           progress: 0,
//           icon: 'faImage',
//           color: 'text-pink-500',
//           bgColor: 'bg-pink-500/20',
//           category: 'engagement'
//         },
//         {
//           id: 'response-time',
//           platform: 'Tüm Platformlar',
//           priority: 'Yüksek',
//           title: 'Yanıt Süresini İyileştirin',
//           description: 'Yorumlara 2 saat içinde yanıt vererek etkileşimi artırın.',
//           impact: '+50% Müşteri Memnuniyeti',
//           effort: 'Orta',
//           timeline: 'Sürekli',
//           status: 'pending',
//           progress: 0,
//           icon: 'faClock',
//           color: 'text-green-500',
//           bgColor: 'bg-green-500/20',
//           category: 'engagement'
//         }
//       ]
//     },
//     content: {
//       title: 'İçerik Önerileri',
//       description: 'İçerik kalitesini artırmak için AI destekli öneriler',
//       recommendations: [
//         {
//           id: 'video-content',
//           platform: 'Tüm Platformlar',
//           priority: 'Yüksek',
//           title: 'Video İçerik Oranını Artırın',
//           description: 'Video içerikler %80 daha fazla etkileşim alıyor.',
//           impact: '+80% Etkileşim',
//           effort: 'Yüksek',
//           timeline: '1 ay',
//           status: 'pending',
//           progress: 0,
//           icon: 'faVideo',
//           color: 'text-red-500',
//           bgColor: 'bg-red-500/20',
//           category: 'content'
//         },
//         {
//           id: 'optimal-timing',
//           platform: 'Tüm Platformlar',
//           priority: 'Orta',
//           title: 'Optimal Paylaşım Saatlerini Kullanın',
//           description: 'Analiz sonuçlarına göre en iyi paylaşım saatlerini belirleyin.',
//           impact: '+45% Erişim',
//           effort: 'Düşük',
//           timeline: '1 hafta',
//           status: 'pending',
//           progress: 0,
//           icon: 'faClock',
//           color: 'text-blue-500',
//           bgColor: 'bg-blue-500/20',
//           category: 'content'
//         }
//       ]
//     }
//   },
//   recommendationDetails: {
//     'reels-strategy': {
//       id: 'reels-strategy',
//       platform: 'Instagram',
//       priority: 'Yüksek',
//       title: 'Reels Stratejisi Geliştirin',
//       description: 'Reels içerikleriniz %40 daha fazla etkileşim alıyor. Günlük 2-3 Reels paylaşımı öneriliyor.',
//       impact: '+25% Etkileşim',
//       effort: 'Orta',
//       timeline: '2 hafta',
//       status: 'pending',
//       progress: 0,
//       icon: 'faVideo',
//       color: 'text-pink-500',
//       bgColor: 'bg-pink-500/20',
//       category: 'growth',
//       steps: [
//         {
//           title: 'İçerik Planlaması',
//           description: 'Günlük 2-3 Reels için içerik konularını belirleyin',
//           completed: false,
//           estimatedTime: '30 dakika'
//         },
//         {
//           title: 'Video Çekimi',
//           description: '15-30 saniyelik kısa videolar çekin',
//           completed: false,
//           estimatedTime: '1 saat'
//         },
//         {
//           title: 'Düzenleme ve Paylaşım',
//           description: 'Videoları düzenleyin ve optimal saatlerde paylaşın',
//           completed: false,
//           estimatedTime: '45 dakika'
//         }
//       ],
//       tips: [
//         'Trend müzikler kullanın',
//         'İlk 3 saniyede dikkat çekin',
//         'Hashtag stratejisi uygulayın',
//         'Etkileşim oranlarını takip edin'
//       ],
//       postExamples: [
//         {
//           type: 'Reels',
//           title: 'Ürün Tanıtım Reels',
//           description: 'Yeni ürününüzü 15 saniyede tanıtın',
//           caption: '🚀 Yeni ürünümüz çıktı! 15 saniyede ne kadar etkili olabileceğini görün 👀 #yeniürün #reels #tanıtım',
//           hashtags: ['#yeniürün', '#reels', '#tanıtım', '#etkileşim'],
//           optimalTime: '19:00-21:00',
//           expectedEngagement: '+60%'
//         }
//       ],
//       metrics: {
//         currentEngagement: 3.2,
//         targetEngagement: 4.0,
//         currentReach: 1200,
//         targetReach: 1800,
//         currentFollowers: 8500,
//         targetFollowers: 10000
//       }
//     },
//     'linkedin-content': {
//       id: 'linkedin-content',
//       platform: 'LinkedIn',
//       priority: 'Yüksek',
//       title: 'Profesyonel İçerik Artırın',
//       description: 'Endüstri analizleri ve profesyonel görüşler %60 daha fazla paylaşım alıyor.',
//       impact: '+35% Paylaşım',
//       effort: 'Düşük',
//       timeline: '1 hafta',
//       status: 'pending',
//       progress: 0,
//       icon: 'faBullhorn',
//       color: 'text-blue-600',
//       bgColor: 'bg-blue-600/20',
//       category: 'growth',
//       steps: [
//         {
//           title: 'İçerik Araştırması',
//           description: 'Endüstri trendlerini ve güncel konuları araştırın',
//           completed: false,
//           estimatedTime: '1 saat'
//         },
//         {
//           title: 'İçerik Yazımı',
//           description: 'Profesyonel ve bilgilendirici içerikler yazın',
//           completed: false,
//           estimatedTime: '2 saat'
//         },
//         {
//           title: 'Paylaşım ve Takip',
//           description: 'İçerikleri paylaşın ve etkileşimleri takip edin',
//           completed: false,
//           estimatedTime: '30 dakika'
//         }
//       ],
//       tips: [
//         'Endüstri uzmanlarından alıntılar yapın',
//         'Veri destekli içerikler paylaşın',
//         'Profesyonel ağınızı genişletin',
//         'Düzenli paylaşım yapın'
//       ],
//       postExamples: [
//         {
//           type: 'Article',
//           title: 'Endüstri Analizi',
//           description: 'Sektörünüzdeki güncel trendleri analiz edin',
//           caption: 'Bu hafta sektörümüzde yaşanan gelişmeleri analiz ettim. Özellikle AI teknolojilerinin etkisi dikkat çekici. #endüstri #analiz #trend',
//           hashtags: ['#endüstri', '#analiz', '#trend', '#profesyonel'],
//           optimalTime: '09:00-11:00',
//           expectedEngagement: '+45%'
//         }
//       ],
//       metrics: {
//         currentEngagement: 2.8,
//         targetEngagement: 3.8,
//         currentReach: 800,
//         targetReach: 1200,
//         currentFollowers: 3200,
//         targetFollowers: 4000
//       }
//     }
//   },
//   charts: {
//     recommendations: {
//       labels: ['Büyüme', 'Etkileşim', 'İçerik', 'Optimizasyon'],
//       datasets: [
//         {
//           label: 'Önerilen Etki',
//           data: [85, 70, 90, 65],
//           backgroundColor: [
//             'rgba(34, 197, 94, 0.8)',
//             'rgba(59, 130, 246, 0.8)',
//             'rgba(168, 85, 247, 0.8)',
//             'rgba(251, 146, 60, 0.8)'
//           ],
//         }
//       ]
//     },
//     progress: {
//       labels: ['Tamamlanan', 'Devam Eden', 'Bekleyen'],
//       datasets: [
//         {
//           label: 'Öneriler',
//           data: [3, 5, 8],
//           backgroundColor: [
//             'rgba(34, 197, 94, 0.8)',
//             'rgba(59, 130, 246, 0.8)',
//             'rgba(156, 163, 175, 0.8)'
//           ],
//         }
//       ]
//     }
//   },
//   aiAnalysis: {
//     recommendations: [
//       {
//         type: 'insight',
//         title: 'Yüksek Potansiyel',
//         description: 'Reels içerikleriniz %40 daha fazla etkileşim alıyor',
//         icon: 'faChartLine',
//         color: 'text-green-400'
//       },
//       {
//         type: 'prediction',
//         title: 'Büyüme Tahmini',
//         description: 'Bu önerileri uygularsanız 3 ayda %60 büyüme bekleniyor',
//         icon: 'faRocket',
//         color: 'text-blue-400'
//       },
//       {
//         type: 'warning',
//         title: 'Dikkat Edilmesi Gerekenler',
//         description: 'Video içerik üretimi zaman alıcı olabilir',
//         icon: 'faExclamationTriangle',
//         color: 'text-yellow-400'
//       }
//     ],
//     progress: [
//       {
//         type: 'insight',
//         title: 'İlerleme Durumu',
//         description: 'Önerilerin %35\'i tamamlandı',
//         icon: 'faCheck',
//         color: 'text-green-400'
//       },
//       {
//         type: 'prediction',
//         title: 'Tamamlanma Tahmini',
//         description: 'Kalan öneriler 2 haftada tamamlanabilir',
//         icon: 'faClock',
//         color: 'text-blue-400'
//       }
//     ]
//   }
// };

// AI Recommendations endpoints
// mock.onGet('/ai-recommendations/recommendations').reply(200, { 
//   success: true, 
//   data: mockAIRecommendationsData.recommendations 
// });

// mock.onGet('/ai-recommendations/recommendation/').reply(200, { 
//   success: true, 
//   data: mockAIRecommendationsData.recommendationDetails[id as keyof typeof mockAIRecommendationsData.recommendationDetails] || null 
// });

// mock.onGet('/ai-recommendations/charts/recommendations').reply(200,{ 
//   success: true, 
//   data: mockAIRecommendationsData.charts.recommendations || [] 
// });

// mock.onGet('/ai-recommendations/charts/progress').reply(200,{ 
//   success: true, 
//   data: mockAIRecommendationsData.charts.progress || [] 
// });

// mock.onGet('/ai-recommendations/ai-analysis/recommendations').reply(200, {
//   success: true, 
//   data: mockAIRecommendationsData.aiAnalysis.recommendations || [] 
// });

// mock.onGet('/ai-recommendations/ai-analysis/progress').reply(200, {
//   success: true, 
//   data: mockAIRecommendationsData.aiAnalysis.progress || [] 
// });

export const aiPredictionsAPI = {
  getPredictions: (businessId: string) => api.get(`/ai-predictions/predictions/${businessId}`),
  getChartData: (businessId: string, chartType: string) => api.get(`/ai-predictions/charts/${businessId}/${chartType}`),
  getAIAnalysis: (businessId: string, chartType: string) => api.get(`/ai-predictions/ai-analysis/${businessId}/${chartType}`),
  // Tüm AI Predictions verisini tek seferde getiren endpoint
  getAllAIPredictionsData: async (businessId: string) => {
    const response = await api.get(`/ai-predictions/${businessId}`);
    return {
      ...response,
      data: {
        ...response.data,
        data: parseMongoDBExtendedJSON(response.data.data || response.data)
      }
    };
  },
};

export const aiRecommendationsAPI = {
  getRecommendations: (businessId: string) => api.get(`/ai-recommendations/recommendations/${businessId}`),
  getRecommendationDetail: (businessId: string, id: string) => api.get(`/ai-recommendations/recommendation/${businessId}/${id}`),
  getChartData: (businessId: string, chartType: string) => api.get(`/ai-recommendations/charts/${businessId}/${chartType}`),
  getAIAnalysis: (businessId: string, chartType: string) => api.get(`/ai-recommendations/ai-analysis/${businessId}/${chartType}`),
};

// ... existing code ...

// const mockTikTokData = {
//   account: {
//     username: 'winnomi_official',
//     followers: 125000,
//     following: 450,
//     videos: 89,
//     profilePicture: 'https://via.placeholder.com/150x150/FF0050/FFFFFF?text=TK',
//     bio: 'sosyal medya analitikleri ve AI destekli içerik önerileri �� #SosyalMedya #AI #Analitik',
//     website: 'https://winnomi.com',
//     isVerified: true,
//     totalLikes: 2500000,
//     totalViews: 15000000
//   },
//   recentVideos: [
//     {
//       id: 1,
//       thumbnail: 'https://via.placeholder.com/300x400/FF0050/FFFFFF?text=Video+1',
//       caption: 'sosyal medya stratejileri hakkında 5 önemli ipucu! 🎯 #SosyalMedya #Strateji #İpucu',
//       likes: 12500,
//       comments: 890,
//       shares: 2340,
//       views: 125000,
//       engagement: 12.5,
//       postedAt: '2024-01-15T10:30:00Z',
//       duration: '0:45',
//       hashtags: ['#SosyalMedya', '#Strateji', '#İpucu', '#Marketing']
//     },
//     {
//       id: 2,
//       thumbnail: 'https://via.placeholder.com/300x400/FF0050/FFFFFF?text=Video+2',
//       caption: 'AI ile içerik üretimi nasıl yapılır? 🤖 #AI #İçerik #Teknoloji',
//       likes: 8900,
//       comments: 567,
//       shares: 1890,
//       views: 98000,
//       engagement: 11.2,
//       postedAt: '2024-01-14T15:45:00Z',
//       duration: '1:20',
//       hashtags: ['#AI', '#İçerik', '#Teknoloji', '#YapayZeka']
//     },
//     {
//       id: 3,
//       thumbnail: 'https://via.placeholder.com/300x400/FF0050/FFFFFF?text=Video+3',
//       caption: 'TikTok algoritması nasıl çalışır? �� #TikTok #Algoritma #SosyalMedya',
//       likes: 15600,
//       comments: 1234,
//       shares: 3450,
//       views: 180000,
//       engagement: 11.3,
//       postedAt: '2024-01-13T20:15:00Z',
//       duration: '0:55',
//       hashtags: ['#TikTok', '#Algoritma', '#SosyalMedya', '#Viral']
//     }
//   ],
//   topVideos: [
//     {
//       id: 4,
//       thumbnail: 'https://via.placeholder.com/300x400/FF0050/FFFFFF?text=Top+Video+1',
//       caption: 'sosyal medya trendleri 2024! 🔥 #Trend #2024 #SosyalMedya',
//       likes: 45000,
//       comments: 3200,
//       shares: 8900,
//       views: 850000,
//       engagement: 6.7,
//       postedAt: '2024-01-10T12:00:00Z',
//       duration: '1:30',
//       hashtags: ['#Trend', '#2024', '#SosyalMedya', '#Viral']
//     },
//     {
//       id: 5,
//       thumbnail: 'https://via.placeholder.com/300x400/FF0050/FFFFFF?text=Top+Video+2',
//       caption: 'İçerik üretimi için 10 altın kural! ✨ #İçerik #Kural #SosyalMedya',
//       likes: 32000,
//       comments: 2100,
//       shares: 5600,
//       views: 620000,
//       engagement: 6.4,
//       postedAt: '2024-01-08T16:30:00Z',
//       duration: '2:15',
//       hashtags: ['#İçerik', '#Kural', '#SosyalMedya', '#Eğitim']
//     }
//   ],
//   engagement: {
//     labels: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'],
//     datasets: [
//       {
//         label: 'Günlük Etkileşim',
//         data: [8.5, 12.3, 15.7, 18.2, 22.1, 25.8, 19.4],
//         borderColor: '#FF0050',
//         backgroundColor: 'rgba(255, 0, 80, 0.1)',
//         borderWidth: 3,
//         fill: true,
//         tension: 0.4,
//         pointBackgroundColor: '#FF0050',
//         pointBorderColor: '#FF0050',
//         pointRadius: 6,
//         pointHoverRadius: 8
//       }
//     ]
//   },
//   reach: {
//     labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
//     datasets: [
//       {
//         label: 'Aylık Erişim',
//         data: [450000, 520000, 680000, 750000, 820000, 890000],
//         backgroundColor: 'rgba(255, 0, 80, 0.8)',
//         borderColor: '#FF0050',
//         borderWidth: 2
//       }
//     ]
//   },
//   contentPerformance: {
//     labels: ['Eğitim', 'Eğlence', 'Trend', 'İçerik', 'Diğer'],
//     datasets: [
//       {
//         data: [35, 25, 20, 15, 5],
//         backgroundColor: [
//           'rgba(255, 0, 80, 0.8)',
//           'rgba(0, 242, 96, 0.8)',
//           'rgba(255, 255, 255, 0.8)',
//           'rgba(0, 0, 0, 0.8)',
//           'rgba(128, 128, 128, 0.8)'
//         ],
//         borderColor: [
//           '#FF0050',
//           '#00F260',
//           '#FFFFFF',
//           '#000000',
//           '#808080'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   sentiment: {
//     labels: ['Pozitif', 'Nötr', 'Negatif'],
//     datasets: [
//       {
//         data: [78, 18, 4],
//         backgroundColor: [
//           'rgba(0, 242, 96, 0.8)',
//           'rgba(107, 114, 128, 0.8)',
//           'rgba(255, 0, 80, 0.8)'
//         ],
//         borderColor: [
//           '#00F260',
//           '#6B7280',
//           '#FF0050'
//         ],
//         borderWidth: 2
//       }
//     ]
//   },
//   aiAnalysis: {
//     engagement: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'TikTok Etkileşim Analizi',
//           message: 'Videolarınızın ortalama etkileşim oranı %12.5. Bu, platform ortalamasının üzerinde!',
//           icon: 'faChartLine',
//           color: 'text-green-400'
//         },
//         {
//           type: 'prediction',
//           title: 'Büyüme Öngörüsü',
//           message: 'Mevcut trend devam ederse, gelecek ay takipçi sayınız %15-20 artabilir.',
//           icon: 'faArrowTrendUp',
//           color: 'text-blue-400'
//         }
//       ]
//     },
//     reach: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Erişim Performansı',
//           message: 'Son 6 ayda erişiminiz %98 arttı. Bu mükemmel bir büyüme oranı!',
//           icon: 'faEye',
//           color: 'text-green-400'
//         },
//         {
//           type: 'warning',
//           title: 'İçerik Çeşitliliği',
//           message: 'Eğitim içerikleriniz %35 oranında. Daha fazla eğlence içeriği ekleyebilirsiniz.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     },
//     contentPerformance: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'İçerik Dağılımı',
//           message: 'Eğitim içerikleriniz en yüksek etkileşimi alıyor. Bu alanda devam edin!',
//           icon: 'faLightbulb',
//           color: 'text-green-400'
//         }
//       ]
//     },
//     sentiment: {
//       insights: [
//         {
//           type: 'insight',
//           title: 'Sentiment Analizi',
//           message: 'Takipçilerinizin %78\'i pozitif tepki veriyor. Bu çok iyi bir oran!',
//           icon: 'faHeart',
//           color: 'text-blue-400'
//         },
//         {
//           type: 'warning',
//           title: 'Negatif Feedback',
//           message: 'Negatif yorumların %60\'ı video kalitesi ile ilgili.',
//           icon: 'faExclamationTriangle',
//           color: 'text-yellow-400'
//         }
//       ]
//     }
//   }
// };

// // ... existing mock endpoints ...

// // TikTok endpoints
// mock.onGet('/tiktok/account').reply(200, {
//   success: true,
//   data: mockTikTokData.account
// });

// mock.onGet('/tiktok/recent-videos').reply(200, {
//   success: true,
//   data: mockTikTokData.recentVideos
// });

// mock.onGet('/tiktok/top-videos').reply(200, {
//   success: true,
//   data: mockTikTokData.topVideos
// });

// mock.onGet('/tiktok/engagement').reply(200, {
//   success: true,
//   data: mockTikTokData.engagement
// });

// mock.onGet('/tiktok/reach').reply(200, {
//   success: true,
//   data: mockTikTokData.reach
// });

// mock.onGet('/tiktok/content-performance').reply(200, {
//   success: true,
//   data: mockTikTokData.contentPerformance
// });

// mock.onGet('/tiktok/sentiment').reply(200, {
//   success: true,
//   data: mockTikTokData.sentiment
// });

// mock.onGet('/tiktok/ai-analysis/engagement').reply(200, {
//   success: true,
//   data: mockTikTokData.aiAnalysis.engagement || []
// });

// mock.onGet('/tiktok/ai-analysis/reach').reply(200, {
//   success: true,
//   data: mockTikTokData.aiAnalysis.reach || []
// });


// mock.onGet('/tiktok/ai-analysis/content-performance').reply(200, {
//   success: true,
//   data: mockTikTokData.aiAnalysis.contentPerformance || []
// });


// mock.onGet('/tiktok/ai-analysis/sentiment').reply(200, {
//   success: true,
//   data: mockTikTokData.aiAnalysis.sentiment || []
// });


const fetchTikTokAnalytics = createAnalyticsFetcher('tiktok');

export const tiktokAPI = {
  getAnalytics: (businessId: string) => api.get(`/tiktok/analytics/${businessId}`),
  getAccountData: async (businessId: string) => api.get(`/tiktok/account/${businessId}`),
  getRecentVideos: async (businessId: string) => api.get(`/tiktok/recent-posts/${businessId}`),
  // Keep function name for compatibility, but fetch from unified endpoint
  getTopVideos: async (businessId: string) => api.get(`/tiktok/top-content/${businessId}`),
  getEngagementData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTikTokAnalytics(businessId))?.engagement ?? null),
  getReachData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTikTokAnalytics(businessId))?.reach ?? null),
  getHashtagsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTikTokAnalytics(businessId))?.hashtags ?? null),
  getContentPerformanceData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTikTokAnalytics(businessId))?.contentPerformance ?? null),
  getSentimentData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTikTokAnalytics(businessId))?.sentiment ?? null),
  getProblemsData: async (businessId: string) =>
    wrapSuccessResponse((await fetchTikTokAnalytics(businessId))?.problems ?? []),
  getAIAnalysis: async (businessId: string, chartType: string) =>
    wrapSuccessResponse(
      normalizeAIAnalysisData((await fetchTikTokAnalytics(businessId))?.aiAnalysis?.[chartType])
    ),
};

// Business Authentication API
export const businessAuthAPI = {
  // Register new business
  register: (data: any) => api.post('/business/register', data),
  
  // Login business
  login: (data: any) => api.post('/business/login', data),
  
  // Verify token
  verifyToken: (token: string) => api.post('/business/verify-token', { token }),
  
  // Refresh token
  refreshToken: (refreshToken: string) => api.post('/business/refresh-token', { refreshToken }),
  
  // Get business profile (requires authentication)
  getProfile: () => {
    console.log('🔵 API CALL: GET /business/profile');
    const request = api.get('/business/profile');
    request.then(
      (response) => {
        console.log('✅ GET /business/profile SUCCESS');
        console.log('✅ GET /business/profile: Full response:', JSON.stringify(response.data, null, 2));
        console.log('✅ GET /business/profile: Response keys:', Object.keys(response.data || {}));
      },
      (error) => {
        console.error('❌ GET /business/profile ERROR:', error);
        console.error('❌ GET /business/profile: Error response:', error.response?.data);
      }
    );
    return request;
  },
  
  // Update business profile (requires authentication)
  updateProfile: (data: any) => {
    console.log('🟢 API CALL: PUT /business/profile', data);
    const request = api.put('/business/profile', data);
    request.then(
      (response) => {
        console.log('✅ PUT /business/profile SUCCESS:', response);
      },
      (error) => {
        console.error('❌ PUT /business/profile ERROR:', error);
      }
    );
    return request;
  },
  
  // Verify email code
  verifyEmailCode: (data: { email: string; code: string }) => api.post('/business/verify-email', data),
  
  // Resend verification code
  resendVerificationCode: (data: { email: string }) => api.post('/business/resend-verification', data),
  
  // Forgot password - send reset code
  forgotPassword: (data: { email: string }) => api.post('/business/forgot-password', data),
  
  // Verify reset code
  verifyResetCode: (data: { email: string; code: string }) => api.post('/business/verify-reset-code', data),
  
  // Reset password
  resetPassword: (data: { token: string; password: string }) => api.post('/business/reset-password', data),
  
  // Google OAuth callback URL
  getGoogleCallbackUrl: () => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/business/google/callback`;
  },

  // Get social media profiles by usernames
  // Timeout: 5 minutes (300000ms) for verification process
  getSocialMediaProfiles: (data: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
    linkedin?: string;
  }) => api.post('/business/social-media-profiles', data, { timeout: 300000 }),

  // Add or update social media accounts
  addSocialMediaAccounts: (data: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
    linkedin?: string;
  }) => api.post('/business/social-media-accounts', data),

  // Sync social media data
  syncSocialMediaData: () => api.post('/business/sync-social-media-data'),
  
  // Upload profile photo (multipart form data)
  uploadProfilePhoto: (formData: FormData) => {
    console.log('📸 API CALL: POST /business/profile-photo');
    const request = api.post('/business/profile-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    request.then(
      (response) => {
        console.log('✅ POST /business/profile-photo SUCCESS:', response);
      },
      (error) => {
        console.error('❌ POST /business/profile-photo ERROR:', error);
      }
    );
    return request;
  },
  
  // Delete profile photo
  deleteProfilePhoto: () => {
    console.log('🗑️ API CALL: DELETE /business/profile-photo');
    const request = api.delete('/business/profile-photo');
    request.then(
      (response) => {
        console.log('✅ DELETE /business/profile-photo SUCCESS:', response);
      },
      (error) => {
        console.error('❌ DELETE /business/profile-photo ERROR:', error);
      }
    );
    return request;
  },
};

export const subscriptionAPI = {
  // Get public key for card encryption
  getPublicKey: () => api.get('/public-key'),
  
  // Get all packages
  getPackages: () => api.get('/packages'),
  
  // Subscribe to a package
  subscribe: (businessId: string, data: {
    encryptedData: string;
    encryptedAesKey: string;
    iv: string;
  }) => api.post(`/subscription/subscribe/${businessId}`, data, { timeout: 180000 }),
  
  // Get subscription
  getSubscription: (businessId: string) => api.get(`/subscription/subscription/${businessId}`),
  
  // Cancel subscription
  cancelSubscription: (businessId: string) => api.post(`/subscription/subscription/${businessId}/cancel`),
  
  // Resume subscription
  resumeSubscription: (businessId: string) => api.post(`/subscription/subscription/${businessId}/resume`),
  
  // Update subscription
  updateSubscription: (businessId: string, data: any) => api.post(`/subscription/subscription/${businessId}/update`, data),
  
  // Get credit
  getCredit: (businessId: string) => api.get(`/subscription/credit/${businessId}`),
  
  // Add additional credit
  addAdditionalCredit: (businessId: string, data: any) => api.post(`/subscription/additional/credit/${businessId}`, data, { timeout: 180000 }),
};

export const creditsAPI = {
  // Estimate credits for analysis
  estimate: (analysis: string, startDate?: string, endDate?: string) => {
    const params: any = { analysis };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/credits/estimate', { params });
  },
};

export const analysisJobsAPI = {
  // Start an analysis job
  start: (analysis: string, startDate?: string, endDate?: string) => {
    const params: any = { analysis };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/analysis-jobs/start', { params });
  },
};

export const notificationAPI = {
  // Get VAPID public key (public endpoint - no auth required)
  getVapidPublicKey: () => api.get('/notifications/vapid-key'),
  
  // Subscribe to WebPush notifications
  subscribeWebPush: (data: {
    endpoint: string;
    keys: {
      p256dh: string;
      auth: string;
    };
  }) => api.post('/notifications/subscribe/webpush', data),
  
  // Subscribe to APNs notifications
  subscribeAPNs: (data: {
    deviceToken: string;
  }) => api.post('/notifications/subscribe/apns', data),
  
  // Get all subscriptions for authenticated business
  getSubscriptions: () => api.get('/notifications/subscriptions'),
  
  // Delete subscription
  deleteSubscription: (id: string) => api.delete(`/notifications/subscriptions/${id}`),
  
  // Unsubscribe (soft delete - deactivate)
  unsubscribe: (id: string) => api.post(`/notifications/subscriptions/${id}/unsubscribe`),
};

export default api; 